<script>
  import { _ } from "svelte-i18n";
  import { BASE_URL } from "../utilities/config.js";
  import { fetchSpeechToken } from "./speechToken.mjs";
  import SpeechToTextOpus from "./components/SpeechToTextOpus.svelte";
  import TextToSpeech2 from "./components/TextToSpeech2.svelte";
  import Instructions from "./components/Instructions.svelte";
  import { onMount, onDestroy, tick } from "svelte";
  import { writable } from "svelte/store";
  import {
    currentPage,
    userId,
    userName,
    assessmentId,
    assessmentDefinitionId,
    assessmentType,
    assessmentState,
    lastRecording,
    sharedTimer,
    sharedTimerMode,
    instanceStatusData,
    invitationNumber,
    initialPhoto,
    exerciseSections,
  } from "../utilities/DataStore";
  import {
    uploadMedia,
    uploadMediaFileName,
    uploadMediaFileUrl,
    uploadExerciseResults,
  } from "./components/UploadResults.svelte";
  import IntroTimer from "./components/IntroTimer.svelte";
  import { router } from "tinro";
  import ProgressDetails from "./components/ProgressDetails.svelte";
  import ProgressDetails3 from "./components/ProgressDetails3.svelte";
  import SharedTimer from "./components/SharedTimer.svelte";
  import { select_option } from "svelte/internal";
  import PhotoVerification from "./components/PhotoVerification.svelte";
  import ImageLoader from "./components/ImageLoader.svelte";
  import { api } from "../utilities/Utils.svelte";
  import { getSoundEffect } from "../utilities/AudioSingleton.js";
  import { sanitizeAssessmentHtml } from "../utilities/TextSanitizer.js";
  import MicSvg from "./components/MicSvg.svelte";
  import AudioPlayback from "./components/AudioPlayback.svelte";
  import PromptAudio from "./components/PromptAudio.svelte";
  import ReportIssueModal from "./components/ReportIssueModal.svelte";
  import FillTheLetters from "./components/FillTheLetters.svelte";
  import DragWords from "./components/DragWords.svelte";
  import { countUnansweredGaps } from "./fillTheGapValidation.mjs";
  import {
    countEmptyLetterBoxes,
    emptyLetterAnswers,
    parseFillTheLetters,
    scoreFillTheLetters,
  } from "./fillTheLetters.mjs";
  import {
    countEmptySlots,
    emptyPlacements,
    parseDragWords,
    scoreDragWords,
  } from "./dragWords.mjs";
  import {
    analyzeGapPlaceholders,
    replaceGapPlaceholdersInText,
  } from "./fillTheGapPlaceholders.mjs";
  import { summarizePhotoVerificationOutcome } from "./photoVerificationOutcome.mjs";
  import {
    hasAudioPrompt,
    isListenRepeatTextHidden,
    promptAudioQueue,
    promptPlaybackSettings,
    shouldHoldPrepareTimer,
  } from "./audioPrompt.mjs";
  import {
    combineSeriesTakes,
    isListenRepeatSeries,
    listenRepeatSentences,
    referenceText,
    seriesRecordingFileName,
  } from "./listenRepeat.mjs";

  export let instanceId;
  export let exerciseId;

  // INTRO page: section stats derived from exerciseSections store, which
  // ProgressDetails3 fills from the definition's categories.
  $: _introSection = $exerciseSections.find(s => s.exercises.some(e => e.id === exerciseId));
  $: sectionIndex = $exerciseSections.findIndex(s => s.exercises.some(e => e.id === exerciseId));
  // Section number is the category's place in the definition, fall back to index.
  $: sectionNumber = _introSection?.sectionNum ?? (sectionIndex >= 0 ? sectionIndex + 1 : 1);

  // Question counts per section, scoped to the selected assessment type.
  $: assessmentTypeKey = ($assessmentType || "").toUpperCase();
  $: questionCountMap = assessmentTypeKey.includes("LISTEN")
    ? { 1: 3, 2: 7, 3: 9, 4: 6 }
    : assessmentTypeKey.includes("SPEAK")
      ? { 1: 2, 2: 4, 3: 3, 4: 3, 5: 3 }
      : { 1: 9, 2: 4, 3: 12 }; // default Reading
  $: introQuestionCount = questionCountMap[sectionNumber] ?? null;
  $: introDurationLimit = $instanceStatusData?.nextExerciseDto?.durationLimit ?? null;

  let introTimerComponent;

  let isCollapsed = false;

  let isNextAllowed = true;
  let showAnswerWarning = false;

  const disableGlobalEvents = () => {
    // Elements marked .copy-paste-allowed (WRITING editor with allowCopyPaste
    // enabled in the exercise definition) opt out of the global block; the
    // editor still logs the attempt to the tracking log.
    document.addEventListener("copy", (event) => {
      if (event.target?.closest?.(".copy-paste-allowed")) return;
      console.log("copy");
      event.preventDefault();
    });
    document.addEventListener("paste", (event) => {
      if (event.target?.closest?.(".copy-paste-allowed")) return;
      console.log("paste");
      event.preventDefault();
    });
    // document.addEventListener("contextmenu", (event) => {
    //   console.log("contextmenu");
    //   event.preventDefault();
    // });
  };

  onDestroy(() => {
    observer?.disconnect();

    // Clean up fallback timer to prevent orphaned callbacks after component destruction
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }

    document.removeEventListener("visibilitychange", handleVisibilityChange);
    clearTimeout(writingWarningTimer);
  });

  let observer;

  let sentinelDiv;

  function defineObserver() {
    const sentinel = sentinelDiv;
    // Create the Intersection Observer
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // If sentinel is in view => we are near the top => expand header
            isCollapsed = false;
          } else {
            // If sentinel is NOT in view => user scrolled down => collapse header
            isCollapsed = true;
          }
        });
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    if (observer && sentinel) {
      // Start observing the sentinel
      observer?.observe(sentinel);
    }

    // Cleanup when component is unmounted
    // return () => {
    //   observer.disconnect();
    // };
  }

  onMount(async () => {
    disableGlobalEvents();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    $lastRecording = undefined;
    loadData();

    if (!!window.SpeechSDK) {
      SpeechSDK = window.SpeechSDK;
      ttsButtonState = "starting";
      initPlayPage();
    } else {
      ttsButtonState = "error";
      console.error("error with SpeechSDK");
      // SDK unavailable: allow the student to skip with an explicit NO_AUDIO
      // result instead of being stuck on a broken speaking exercise.
      canRecordNoAudio = true;
      noAudioReason = "SDK_MISSING";
    }
  });

  // document.addEventListener("click", () => {
  //   const context = new (window.AudioContext || window.webkitAudioContext)();
  //   context.resume();
  // });

  let audioDisabledNeedClick = false;
  let audioRetryCount = 0;
  const MAX_AUDIO_RETRIES = 3;
  const AUDIO_RETRY_DELAY = 1000; // 1 second between retries

  // Module-level variables for fallback timer management to prevent duplicate submissions
  let speechEndDetectedFired = false;
  let fallbackTimer = null;
  let isSubmitting = false;

  // UI-level lock for the whole submit lifecycle (from the moment the user
  // commits to submitting until the upload resolves). Drives the disabled +
  // spinner button state and blocks parallel triggers (re-click, timer expiry,
  // the duplicate stop path). Distinct from `isSubmitting`, which guards only
  // the in-flight upload itself.
  let isProcessing = false;

  // Non-blocking message shown when a result upload fails so the user can retry
  // from the same exercise instead of being advanced or sent to Overview.
  let submitErrorMessage = "";

  // Speech-capture failure state. When a speaking exercise cannot be completed
  // (mic denied, token/recognition failure, no speech detected), we offer a
  // "skip" that records an explicit NO_AUDIO result so the exercise is never
  // silently missing. canRecordNoAudio gates the skip button so it is NOT shown
  // for upload/advance failures (where the student did speak and should retry).
  let canRecordNoAudio = false;
  let noAudioReason = "";

  // Early-stop protection
  let recordingStartTime = null;
  let showEarlyStopWarning = false;

  function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  function tryPlayAudio() {
    if (!audioPlayer) return;
    
    console.log("Attempting to play audio, retry count:", audioRetryCount);
    audioPlayer.play()
      .then(() => {
        console.log("Audio playing successfully");
        audioDisabledNeedClick = false;
        audioRetryCount = 0;
      })
      .catch((err) => {
        console.warn("Audio play failed:", err);
        if (audioRetryCount < MAX_AUDIO_RETRIES) {
          audioRetryCount++;
          setTimeout(tryPlayAudio, AUDIO_RETRY_DELAY);
        } else {
          console.error("Audio play failed after", MAX_AUDIO_RETRIES, "retries");
          audioDisabledNeedClick = true;
        }
      });
  }

  $: if (audioPlayer && exerciseDetailData?.audioFileName) {
    // Reset retry count for new audio
    audioRetryCount = 0;
    
    // Preload audio
    audioPlayer.preload = "auto";
    audioPlayer.load();

    let delay = 500; // Small delay to allow buffering

    if (isIOS()) {
      // iOS requires a user interaction to play audio
      setTimeout(() => {
        audioDisabledNeedClick = true;
      }, delay);
    } else {
      // Wait for the audio to be loaded before attempting play
      audioPlayer.onloadeddata = () => {
        console.log("Audio loaded, attempting to play");
        setTimeout(() => {
          tryPlayAudio();
        }, delay);
      };
      
      // Add error handler for loading failures
      audioPlayer.onerror = (e) => {
        console.error("Audio loading error:", e);
        audioDisabledNeedClick = true;
      };
      
      // Fallback: if onloadeddata doesn't fire within 5 seconds, show click prompt
      setTimeout(() => {
        if (audioPlayer && audioPlayer.paused && !audioDisabledNeedClick) {
          console.warn("Audio didn't autoplay, requiring user interaction");
          audioDisabledNeedClick = true;
        }
      }, 5000);
    }
  }

  function resumeAudio() {
    console.log("resumeAudio");
    if (audioPlayer && audioDisabledNeedClick) {
      audioRetryCount = 0; // Reset retry count on manual interaction
      tryPlayAudio();
    }
  }

  let exerciseStatus = "NEW";
  let hasLoadedExerciseOnce = false;
  let exerciseDetail;
  let exerciseDetailData;
  $: isMCCombined = exerciseDetail?.exerciseType === 'MULTICHOICE_COMBINED';
  $: currentQuestionNumber = ($instanceStatusData?.countUniqueDone || 0) + 1;
  $: totalQuestionCount = $instanceStatusData?.countTotal || "-";
  let errorMsg;

  let parts = [];
  let aggregateScore;

  let speechToTextOpus = undefined;
  let audioControlDuration = writable(undefined);
  let recognitionResults = [];
  let recognitionResultsFinal = writable([]);
  let ttsButtonState;
  let recognizer;

  let ttsInstance = undefined;

  let ttsToken;
  let ttsRegion = "eastasia";
  let textToDisplay;
  let textToRead;
  let soundEffect;

  let selectedOption;
  let selectedOptionList = [];
  let audioPlayer;

  // ── FILL_THE_GAP state ──
  // One answer string per gap (indexed like exerciseDetailData.gaps). The
  // question/text HTML with inline fields is prebuilt in loadData — authors
  // put __N__ placeholders in either field, so both are scanned.
  let gapAnswers = [];
  let gapQuestionHtml = "";
  let gapTextHtml = "";
  let gapFallbackIndexes = []; // gaps whose __N__ placeholder appears in neither field
  let showIncompleteGapModal = false; // confirm submitting with blanks left

  // Layout follows what the placeholders did: the answer column only exists
  // when something still needs it (a question to show, or gaps that were not
  // placed inline), otherwise the passage takes the full width.
  $: gapHasFallback = gapFallbackIndexes.length > 0;
  $: gapHasQuestion =
    !!gapQuestionHtml && hasDisplayText(exerciseDetailData?.question);
  $: gapHasAnswerCol = gapHasFallback || gapHasQuestion;
  $: gapPromptColClass = !gapHasAnswerCol
    ? "col-12"
    : gapHasFallback
      ? "col-lg-7 col-12"
      : "col-lg-6 col-12";
  $: gapAnswerColClass = gapHasFallback ? "col-lg-5 col-12" : "col-lg-6 col-12";

  // ── FILL_THE_LETTERS / DRAG_WORDS state ──
  // Both are authored inline: a [[...]] in the question or text marks the
  // missing letters of a word (FILL_THE_LETTERS) or a slot for a word card
  // (DRAG_WORDS). The definition is parsed once in loadData; the answers live
  // here so the submit paths can score them.
  let letterExercise = null;
  let letterAnswers = []; // [block][letter]
  let dragExercise = null;
  let dragPlacements = []; // [slot] -> bank card index, null while empty
  $: isTextBlankType =
    exerciseDetail?.exerciseType === "FILL_THE_LETTERS" ||
    exerciseDetail?.exerciseType === "DRAG_WORDS";

  // ── WRITING state ──
  let writingText = "";
  let writingTrackingLog = []; // full edit history + security events (uploaded as rawData)
  let writingStartTime = null;
  let writingWarning = "";
  let writingWarningTimer = null;
  let showUnderWordCountModal = false;

  $: writingWordCount = countWords(writingText);
  // Soft minimum: explicit minWords from the definition, otherwise 5 words per
  // minute of answer time. 0 = no minimum (no confirm dialog).
  $: writingMinWords =
    exerciseDetail?.exerciseType === "WRITING"
      ? exerciseDetailData?.minWords ||
        (exerciseDetail?.durationLimit
          ? Math.max(1, Math.round((exerciseDetail.durationLimit / 60) * 5))
          : 0)
      : 0;
  $: writingAllowCopyPaste = !!exerciseDetailData?.allowCopyPaste;

  // How many gaps are still blank. Drives the Continue-time warning and the
  // confirmation dialog — never the button's enabled state.
  // NB: reference exerciseDetailData.gaps and gapAnswers directly so Svelte
  // tracks them as dependencies — going through a helper would hide gapAnswers
  // from the compiler and the count would never update as the student types.
  $: unansweredGapCount =
    exerciseDetail?.exerciseType === "FILL_THE_GAP"
      ? countUnansweredGaps(exerciseDetailData?.gaps || [], gapAnswers)
      : 0;

  // Blanks never disable CONTINUE for FILL_THE_GAP. An incomplete form is a
  // legitimate result: the student confirms it (onContinueClick), and timer
  // expiry submits it outright. The other answer-then-submit types keep their
  // completeness gate.
  $: if (exerciseDetail?.exerciseType === "FILL_THE_GAP") {
    isNextAllowed = true;
  }
  // Retract the warning the moment the last blank is filled, rather than
  // leaving it on screen until the student presses Continue again.
  $: if (
    exerciseDetail?.exerciseType === "FILL_THE_GAP" &&
    unansweredGapCount === 0
  ) {
    showAnswerWarning = false;
  }
  // Same soft rule for the empty letter boxes and word slots: they warn and
  // ask on Continue (onContinueClick), they never disable it. Referenced
  // directly for the same dependency-tracking reason as unansweredGapCount.
  $: unansweredLetterCount =
    exerciseDetail?.exerciseType === "FILL_THE_LETTERS"
      ? countEmptyLetterBoxes(letterExercise?.blocks || [], letterAnswers)
      : 0;
  $: emptyDragSlotCount =
    exerciseDetail?.exerciseType === "DRAG_WORDS"
      ? countEmptySlots(dragPlacements)
      : 0;
  $: if (isTextBlankType && unansweredLetterCount === 0 && emptyDragSlotCount === 0) {
    showAnswerWarning = false;
  }
  $: if (exerciseDetail?.exerciseType === "WRITING") {
    isNextAllowed = writingWordCount > 0;
  }

  function countWords(text) {
    return String(text || "")
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Builds one FILL_THE_GAP HTML block: every __N__ token (1-based index into
  // gaps) is replaced with an inline <input> or <select> so the answer is typed
  // directly inside the sentence. The markup is injected via {@html}, so events
  // are captured by delegation on the wrapper (handleGapFieldEvent). Replaced
  // indexes are recorded in placedIndexes (shared across question + text); gaps
  // placed nowhere are rendered as a labeled fallback list instead of being
  // silently lost.
  //
  // The author's HTML is normalized before the tokens are swapped, so the
  // field markup built here is not affected by that normalization.
  function replaceGapTokens(text, gaps, placedIndexes) {
    return replaceGapPlaceholdersInText(
      fixPunctuation(text),
      (token, num) => {
        const index = Number.parseInt(num, 10) - 1;
        if (index < 0 || index >= gaps.length) {
          return token; // no such gap — keep the token visible so authors can spot it
        }
        if (placedIndexes.has(index)) {
          // A repeated token would give one gap two fields fighting over the
          // same answer. Only the first wins; the rest stay visible as text so
          // the authoring mistake is obvious (the admin editor warns about it).
          return token;
        }
        placedIndexes.add(index);
        return renderGapField(index, gaps[index]);
      },
    );
  }

  // Inline counterpart of the fallback <input>/<select> below the passage:
  // same classes, same delegated events, same value source (gapAnswers is
  // written by handleGapFieldEvent via data-gap-index).
  function renderGapField(index, gap) {
    const label = escapeHtml(gap?.label || `Gap ${index + 1}`);
    const common =
      `class="gap-field gap-inline-field" data-gap-index="${index}" ` +
      `aria-label="${label}" title="${label}"`;

    if (gap?.fieldType === "DROPDOWN") {
      const options = (gap?.options || [])
        .map((option) => {
          const value = escapeHtml(option);
          return `<option value="${value}">${value}</option>`;
        })
        .join("");
      return `<select ${common}><option value=""></option>${options}</select>`;
    }

    return (
      `<input type="text" ${common} value="" autocomplete="off" ` +
      `autocapitalize="off" autocorrect="off" spellcheck="false" />`
    );
  }

  // Delegated input/change handler for the inline fields injected via {@html}.
  function handleGapFieldEvent(event) {
    const indexAttr = event?.target?.dataset?.gapIndex;
    if (indexAttr === undefined) return;
    const index = Number.parseInt(indexAttr, 10);
    if (Number.isNaN(index)) return;
    gapAnswers[index] = event.target.value;
    gapAnswers = gapAnswers;
  }

  function setGapAnswer(index, value) {
    gapAnswers[index] = value;
    gapAnswers = gapAnswers;
  }

  // Client-side gap evaluation, run once at submit time: trimmed student input
  // tested against the gap's answer regex — same case-sensitive RegExp.test the
  // admin regex tester uses, so admin-verified patterns behave identically here.
  function evaluateGapAnswer(pattern, input) {
    if (!pattern) return false;
    try {
      return new RegExp(pattern).test(input);
    } catch (error) {
      console.error("Invalid gap answer regex", pattern, error);
      return false;
    }
  }

  function logWritingEvent(type, extra = {}) {
    writingTrackingLog.push({
      t: writingStartTime ? Date.now() - writingStartTime : 0,
      type,
      ...extra,
    });
    writingTrackingLog = writingTrackingLog;
  }

  // Minimal common prefix/suffix diff so the full edit history stays
  // proportional to what was actually typed (a full-text snapshot per keystroke
  // grows O(n^2)). Replay: text = text.slice(0, s) + i + text.slice(s + d)
  function diffTexts(oldText, newText) {
    let start = 0;
    const maxStart = Math.min(oldText.length, newText.length);
    while (start < maxStart && oldText[start] === newText[start]) start++;
    let oldEnd = oldText.length;
    let newEnd = newText.length;
    while (
      oldEnd > start &&
      newEnd > start &&
      oldText[oldEnd - 1] === newText[newEnd - 1]
    ) {
      oldEnd--;
      newEnd--;
    }
    return { s: start, d: oldEnd - start, i: newText.slice(start, newEnd) };
  }

  function handleWritingInput(event) {
    const newText = event.target.value;
    logWritingEvent("INPUT", diffTexts(writingText, newText));
    writingText = newText;
  }

  function showWritingWarning(message) {
    writingWarning = message;
    clearTimeout(writingWarningTimer);
    writingWarningTimer = setTimeout(() => {
      writingWarning = "";
    }, 3000);
  }

  // Guard for clipboard/drag actions on the WRITING editor: always logged;
  // blocked (with a visible warning) unless the definition allows copy & paste.
  function handleWritingGuard(event, type, warningKey) {
    logWritingEvent(type, { data: writingAllowCopyPaste ? "ALLOWED" : "BLOCKED" });
    if (writingAllowCopyPaste) return;
    event.preventDefault();
    showWritingWarning($_(warningKey));
  }

  // Tab switches / window minimize during a WRITING exercise are recorded.
  function handleVisibilityChange() {
    if (exerciseDetail?.exerciseType !== "WRITING") return;
    if (document.visibilityState === "hidden") {
      logWritingEvent("TAB_HIDDEN");
    } else {
      logWritingEvent("TAB_VISIBLE");
      showWritingWarning($_("TAB_LEAVE_WARNING"));
    }
  }

  // Compact per-category counters stored in the score data so proctors can see
  // suspicious activity at a glance without replaying the rawData log.
  function summarizeWritingLog(log) {
    const summary = {
      inputEvents: 0,
      focusLost: 0,
      tabHidden: 0,
      pasteBlocked: 0,
      pasteAllowed: 0,
      copyBlocked: 0,
      copyAllowed: 0,
      cutBlocked: 0,
      cutAllowed: 0,
      dropBlocked: 0,
      dropAllowed: 0,
    };
    (log || []).forEach((e) => {
      if (e.type === "INPUT") summary.inputEvents++;
      else if (e.type === "FOCUS_LOST") summary.focusLost++;
      else if (e.type === "TAB_HIDDEN") summary.tabHidden++;
      else if (e.type === "PASTE_ATTEMPT")
        e.data === "ALLOWED" ? summary.pasteAllowed++ : summary.pasteBlocked++;
      else if (e.type === "COPY_ATTEMPT")
        e.data === "ALLOWED" ? summary.copyAllowed++ : summary.copyBlocked++;
      else if (e.type === "CUT_ATTEMPT")
        e.data === "ALLOWED" ? summary.cutAllowed++ : summary.cutBlocked++;
      else if (e.type === "DROP_ATTEMPT")
        e.data === "ALLOWED" ? summary.dropAllowed++ : summary.dropBlocked++;
    });
    return summary;
  }

  const fixPunctuation = sanitizeAssessmentHtml;

  let passageTabs = [];
  let activePassageIndex = 0;
  let swipeTouchStartX = 0;
  $: hasMcCombinedPassageContent =
    (passageTabs || []).some((p) => hasDisplayText(p?.content)) ||
    hasDisplayText(exerciseDetailData?.text);

  function onPassageTouchStart(e) {
    swipeTouchStartX = e.touches[0].clientX;
  }

  function onPassageTouchEnd(e) {
    const dx = e.changedTouches[0].clientX - swipeTouchStartX;
    if (Math.abs(dx) < 40) return;
    if (dx < 0 && activePassageIndex < (passageTabs?.length ?? 1) - 1) {
      activePassageIndex += 1;
    } else if (dx > 0 && activePassageIndex > 0) {
      activePassageIndex -= 1;
    }
  }

  function onPassageClick(e) {
    if (!passageTabs?.length || passageTabs.length < 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const zone = rect.width * 0.18;
    if (relX < zone && activePassageIndex > 0) {
      activePassageIndex -= 1;
    } else if (relX > rect.width - zone && activePassageIndex < passageTabs.length - 1) {
      activePassageIndex += 1;
    }
  }

  // Parses dialogue passages ([time] Speaker: message) into structured chat data
  function parseDialogueContent(content) {
    if (!content) return null;
    const plain = content
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    const lines = plain.split('\n').map(l => l.trim()).filter(Boolean);

    // Match: [4:16 p.m.] Edward: message
    const lineRe = /^\[([^\]]+)\]\s+([^:]+):\s+(.*)/;
    const messages = [];
    for (const line of lines) {
      const m = line.match(lineRe);
      if (m) {
        messages.push({ time: m[1].trim(), speaker: m[2].trim(), text: m[3].trim() });
      }
    }
    if (messages.length < 2) return null;
    const speakers = [...new Set(messages.map(m => m.speaker))];
    if (speakers.length < 2) return null;
    return { messages, firstSpeaker: speakers[0] };
  }

  // Parses email-format passages (TO:/FROM:/SUBJECT: headers) into structured data
  function parseEmailContent(content) {
    if (!content) return null;
    const plain = content
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    const lines = plain.split('\n').map(l => l.trim()).filter(Boolean);

    const headers = [];
    let bodyStart = 0;
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(/^(TO|FROM|SUBJECT)\s*:\s*(.*)/i);
      if (m) {
        headers.push({ label: m[1].toUpperCase(), value: m[2] });
        bodyStart = i + 1;
      } else if (headers.length > 0) {
        break; // stop at first non-header line after headers started
      }
    }
    if (headers.length === 0) return null;
    // Build body as HTML paragraphs from remaining lines
    const bodyLines = lines.slice(bodyStart);
    const bodyHtml = bodyLines
      .map(l => `<p style="margin:0 0 0.75rem;">${l}</p>`)
      .join('');
    return { headers, bodyHtml };
  }

  let photoVerification;
  let photoVerificationOutcome = null;

  // PHOTO_VERIFICATION gates its own Submit button: until the capture attempt
  // reports back there is nothing to submit.
  function onPhotoVerificationInit() {
    photoVerificationOutcome = null;
    isNextAllowed = false;
  }

  function onPhotoUploaded() {
    isNextAllowed = true;
  }

  // The terminal result of the photo step, success or failure. Under
  // `skipToNext` the camera UI is hidden and the candidate only ever sees
  // "Please wait a moment.", so this handler is the sole thing that can advance
  // them — it was dropped by a bad merge hunk in 640406c and candidates have
  // been stranded on that screen since.
  //
  // Advancing is idempotent by construction: the component emits at most one
  // outcome per attempt, stopFromTimerOnly refuses re-entry while isProcessing,
  // and submitResultSkip refuses re-entry while isSubmitting. A duplicate
  // event, a late upload landing after the watchdog, or the candidate pressing
  // Submit at the same moment all collapse into a single submission.
  function onPhotoVerificationComplete(detail) {
    photoVerificationOutcome = summarizePhotoVerificationOutcome(detail);

    // Never leave the candidate holding a disabled Submit button. A failed
    // capture still has to be submittable by hand if the auto-advance below
    // is blocked or its submission fails.
    isNextAllowed = true;

    if (photoVerificationOutcome.status !== "SUCCESS") {
      // A skipped verification is a real gap in the record, not a non-event.
      trackError(
        "photo verification failed",
        photoVerificationOutcome.message || photoVerificationOutcome.reason,
        {
          photoVerificationReason: photoVerificationOutcome.reason,
          photoVerificationAttempt: photoVerificationOutcome.attempt,
        },
      );
    }

    // Without skipToNext the candidate drives the step with the Submit button.
    if (!exerciseDetailData?.skipToNext) return;

    stopFromTimerOnly();
  }

  // Token fetch retry configuration
  const TOKEN_MAX_RETRIES = 3;
  const TOKEN_RETRY_DELAY = 1000; // 1 second between retries
  let tokenRetryCount = 0;

  function initPlayPage() {
    tokenRetryCount = 0;
    fetchTokenWithRetry();
  }

  function fetchTokenWithRetry() {
    fetchSpeechToken(BASE_URL)
      .then((data) => {
        ttsToken = data.token;
        ttsRegion = data.region;
        ttsButtonState = "ready";
        tokenRetryCount = 0; // Reset on success
      })
      .catch((error) => {
        // Enhanced error logging for TrackJS
        console.error("Azure token fetch failed:", {
          error: error?.message || error,
          attempt: tokenRetryCount + 1,
          maxRetries: TOKEN_MAX_RETRIES,
          exerciseId,
          instanceId,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
        
        // Retry logic
        if (tokenRetryCount < TOKEN_MAX_RETRIES) {
          tokenRetryCount++;
          console.log(`Retrying token fetch in ${TOKEN_RETRY_DELAY}ms (attempt ${tokenRetryCount}/${TOKEN_MAX_RETRIES})`);
          setTimeout(() => fetchTokenWithRetry(), TOKEN_RETRY_DELAY);
        } else {
          console.error("Azure token fetch failed after max retries:", {
            totalAttempts: TOKEN_MAX_RETRIES,
            exerciseId,
            instanceId,
          });
          ttsButtonState = "error";
          // The speech token never came back. Let the student skip (records an
          // explicit NO_AUDIO result) rather than being stranded with no result.
          canRecordNoAudio = true;
          noAudioReason = "TOKEN_FAILURE";
          submitErrorMessage = $_("MICROPHONE_ERROR");
          trackError("Azure token fetch failed after max retries", error, {
            step: "token",
            noAudioReason: "TOKEN_FAILURE",
          });
        }
      });
  }

  // Function to refresh token (can be called when token expires during recognition)
  function refreshToken() {
    console.log("Refreshing Azure token...");
    tokenRetryCount = 0;
    ttsButtonState = "starting";
    fetchTokenWithRetry();
  }

  // Scope every subsequent TrackJS report (handled or uncaught) to this test
  // session so failures are searchable by instance/exercise/invitation.
  function setTrackJsContext() {
    try {
      if (!window.TrackJS) return;
      window.TrackJS.addMetadata("instanceId", String(instanceId ?? ""));
      window.TrackJS.addMetadata("exerciseId", String(exerciseId ?? ""));
      window.TrackJS.addMetadata("exerciseType", exerciseDetail?.exerciseType ?? "");
      window.TrackJS.addMetadata("invitationNumber", String($invitationNumber ?? ""));
      window.TrackJS.addMetadata("userId", String($userId ?? ""));
    } catch (err) {
      console.warn("setTrackJsContext failed", err);
    }
  }

  // Report a handled error to TrackJS. Without this, caught submit failures only
  // live in the console/telemetry timeline and never create a TrackJS report.
  function trackError(context, error, extra = {}) {
    console.error(context, error, extra);
    try {
      if (!window.TrackJS) return;
      setTrackJsContext();
      Object.entries(extra).forEach(([k, v]) =>
        window.TrackJS.addMetadata(k, String(v)),
      );
      window.TrackJS.track(
        error instanceof Error ? error : new Error(`${context}: ${error}`),
      );
    } catch (err) {
      console.warn("trackError failed", err);
    }
  }

  async function loadData(targetExerciseId = exerciseId, suppressSpinner = false) {
    try {
      if (!suppressSpinner) {
        exerciseStatus = "LOADING";
      }

      const response = await api.get(
        `public/assessment/definition/${$assessmentDefinitionId}/exercise/${targetExerciseId}`,
      );

      exerciseDetail = await response.json();
      exerciseDetailData = JSON.parse(exerciseDetail.data);

      if (exerciseDetail.prepareLimit) {
        $sharedTimer = exerciseDetail.prepareLimit;
      } else {
        $sharedTimer = 60;
      }

      $sharedTimerMode = "PREPARE";

      console.log("exerciseDetail", exerciseDetail);
      console.log("exerciseDetailData", exerciseDetailData);
      hasLoadedExerciseOnce = true;
      exerciseStatus = "OK";

      setTrackJsContext();

      selectedOptionList = [];
      selectedOption = undefined;
      showAnswerWarning = false;
      isNextAllowed = true;
      // Clear the submit lock and any stale error when (re)entering an exercise.
      isProcessing = false;
      submitErrorMessage = "";
      // Reset speech-capture failure state for the new exercise.
      canRecordNoAudio = false;
      noAudioReason = "";
      lastErrorPhase = "upload";
      // Must not leak into the next exercise's result payload.
      photoVerificationOutcome = null;
      // Per-exercise audio state: audioFinished in the result and the
      // prompt-recording preparation hold both read it.
      audioStatus = "NOT_STARTED";
      promptAudioDone = false;
      promptStep = 0;
      // LISTEN_REPEAT series: start from the first sentence. A series left
      // half-way is not saved, so it starts again on return.
      sentenceIndex = 0;
      seriesTakes = [];
      // The component is reused across exercises: a recording left from the
      // previous exercise must not be taken for this one's.
      $lastRecording = undefined;

      if (
        exerciseDetail.exerciseType === "MULTICHOICE_COMBINED" ||
        exerciseDetail.exerciseType === "MULTICHOICE_SELECT" ||
        exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" ||
        exerciseDetail.exerciseType === "FILL_THE_GAP" ||
        exerciseDetail.exerciseType === "WRITING"
      ) {
        isNextAllowed = false;
      }

      // FILL_THE_GAP: reset answers and prebuild the HTML with inline fields.
      // Placeholders are honored in both the question and the text — authors
      // use either field for the sentence containing the gaps.
      gapAnswers = [];
      gapQuestionHtml = "";
      gapTextHtml = "";
      gapFallbackIndexes = [];
      if (exerciseDetail.exerciseType === "FILL_THE_GAP") {
        const gaps = exerciseDetailData?.gaps || [];
        gapAnswers = gaps.map(() => "");
        // Question is scanned first, so a gap tokened in both fields is placed
        // in the question — analyzeGapPlaceholders is given the same order so
        // the fallback list agrees with the markup (and with the editor).
        const placed = new Set();
        gapQuestionHtml = replaceGapTokens(
          exerciseDetailData?.question,
          gaps,
          placed,
        );
        gapTextHtml = replaceGapTokens(exerciseDetailData?.text, gaps, placed);
        gapFallbackIndexes = analyzeGapPlaceholders(
          [
            { name: "question", value: exerciseDetailData?.question },
            { name: "text", value: exerciseDetailData?.text },
          ],
          gaps.length,
        ).fallbackIndexes;
      }
      showIncompleteGapModal = false;

      // FILL_THE_LETTERS / DRAG_WORDS: parse the [[...]] markers and start
      // with every box / slot empty. Neither type gates CONTINUE, so they are
      // not in the isNextAllowed = false list above. The bank is shuffled
      // per candidate and stays the same across a reload.
      letterExercise = null;
      letterAnswers = [];
      dragExercise = null;
      dragPlacements = [];
      if (exerciseDetail.exerciseType === "FILL_THE_LETTERS") {
        letterExercise = parseFillTheLetters(exerciseDetailData, fixPunctuation);
        letterAnswers = emptyLetterAnswers(letterExercise.blocks);
      } else if (exerciseDetail.exerciseType === "DRAG_WORDS") {
        dragExercise = parseDragWords(exerciseDetailData, {
          sanitize: fixPunctuation,
          seed: `${instanceId}:${targetExerciseId}`,
        });
        dragPlacements = emptyPlacements(dragExercise.slots);
      }

      // WRITING: reset editor + tracking state; log timestamps are relative to
      // this moment (when the exercise was shown).
      writingText = "";
      writingTrackingLog = [];
      writingStartTime = Date.now();
      writingWarning = "";
      showUnderWordCountModal = false;

      aggregateScore = {};

      // Helper for splitting passage text by multiple possible page-break markers
      const splitPassageText = (value) => {
        if (!value || typeof value !== "string") return [];

        // Use factory functions so each regex is fresh (no stale lastIndex)
        const separatorPatterns = [
          () => /<hr\b[^>]*\/?>/gi,          // <hr>, <hr/>, <hr class="..."> etc.
          () => /-{5,}/g,                     // ----- or more hyphens
          () => /\* \* \*/g,                  // * * * separator
          () => /<div[^>]*page-break[^>]*>.*?<\/div>/gi,
          () => /\f/g,                        // form-feed
        ];

        let sections = [value];

        for (const makeRe of separatorPatterns) {
          const re = makeRe();
          if (sections.some((piece) => makeRe().test(piece))) {
            sections = sections
              .flatMap((piece) => piece.split(makeRe()))
              .map((p) => p.trim())
              .filter((p) => p.length > 0);
            if (sections.length > 1) break;
          }
        }

        // fallback: if there are visible hard breaks with >2 empty lines
        if (sections.length === 1 && /(?:\r?\n){3,}/.test(value)) {
          sections = value
            .split(/(?:\r?\n){3,}/)
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        }

        return sections;
      };

      // Multi-passage tab support
      passageTabs = [];
      activePassageIndex = 0;

      if (exerciseDetailData?.passages?.length) {
        passageTabs = exerciseDetailData.passages.map((p, i) => ({
          title: p.title || `Passage ${i + 1}`,
          content: p.text || p.content || "",
        }));
      } else if (exerciseDetailData?.articles?.length) {
        passageTabs = exerciseDetailData.articles.map((p, i) => ({
          title: p.title || `Article ${i + 1}`,
          content: p.text || p.content || "",
        }));
      } else if (exerciseDetailData?.texts?.length) {
        passageTabs = exerciseDetailData.texts.map((t, i) => ({
          title: t.title || `Passage ${i + 1}`,
          content: t.text || t.content || "",
        }));
      } else if (exerciseDetailData?.text && typeof exerciseDetailData.text === "string") {
        const passages = splitPassageText(exerciseDetailData.text);
        if (passages.length > 1) {
          passageTabs = passages.map((passage, i) => ({
            title: `Passage ${i + 1}`,
            content: passage,
          }));
        } else {
          passageTabs = [
            {
              title: exerciseDetailData.title || "Passage 1",
              content: exerciseDetailData.text,
            },
          ];
        }
      } else if (
        exerciseDetail.exerciseType !== "MULTICHOICE_COMBINED" &&
        exerciseDetailData?.question &&
        typeof exerciseDetailData.question === "string"
      ) {
        const passages = splitPassageText(exerciseDetailData.question);
        if (passages.length > 1) {
          passageTabs = passages.map((passage, i) => ({
            title: `Passage ${i + 1}`,
            content: passage,
          }));
        } else {
          passageTabs = [
            {
              title: exerciseDetailData.title || "Passage 1",
              content: exerciseDetailData.question,
            },
          ];
        }
      }

      defineObserver();

      if (suppressSpinner) {
        initPlayPage();
      }
    } catch (error) {
      console.error("catch error", error);
      errorMsg = "ERROR_LOADING_EXERCISE";
      exerciseDetail = undefined;
      exerciseStatus = "ERROR";
    }

    router.goto(`/progress/${instanceId}/${targetExerciseId}`);
  }

  // Which step failed last, so the retry buttons (submitRetry / retrySubmitSkip)
  // know whether to re-run the upload or just re-attempt advancing to the next
  // exercise. Set at every catch site.
  let lastErrorPhase = "upload"; // "upload" | "advance"

  // Bounded retry for the status fetch. ky already retries 5xx; this also covers
  // thrown network/parse errors (offline/DNS/timeout) that ky surfaces as throws.
  const GO_NEXT_MAX_ATTEMPTS = 4;
  const GO_NEXT_RETRY_BASE_MS = 600;
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function goToNextExercise() {
    ttsButtonState = "starting";

    let data;
    let lastError;
    for (let attempt = 1; attempt <= GO_NEXT_MAX_ATTEMPTS; attempt++) {
      try {
        data = await api
          .get(`public/assessment/instance/${instanceId}/status`)
          .json();
        lastError = undefined;
        break;
      } catch (err) {
        lastError = err;
        console.error("[SCORING-DIAG] goToNextExercise status fetch failed", {
          instanceId,
          exerciseId,
          step: "advance",
          attempt,
          maxAttempts: GO_NEXT_MAX_ATTEMPTS,
          error: err?.message || err,
          timestamp: new Date().toISOString(),
        });
        if (attempt < GO_NEXT_MAX_ATTEMPTS) {
          await sleep(GO_NEXT_RETRY_BASE_MS * attempt);
        }
      }
    }

    // Every attempt failed: do NOT abandon the remaining exercises by jumping to
    // Overview (that is what made exercises appear "skipped"). The result was
    // already saved; surface a retry affordance and stay on this exercise. The
    // REFRESH / RETRY buttons route back here via retryGoToNextExercise().
    if (lastError) {
      lastErrorPhase = "advance";
      isProcessing = false;
      isSubmitting = false;
      ttsButtonState = "error";
      submitErrorMessage = $_("UPLOAD_FAILED_RETRY");
      trackError("goToNextExercise status fetch failed", lastError, {
        step: "advance",
        nextExerciseId: $instanceStatusData?.nextExerciseId,
      });
      return;
    }

    $instanceStatusData = data;
    if (data?.nextExerciseId) {
      exerciseId = data.nextExerciseId;
      loadData(data.nextExerciseId, true).then(async () => {
        await tick();
        requestAnimationFrame(() => {
          sentinelDiv?.scrollIntoView({ behavior: "auto", block: "start" });
          window.scrollTo({ top: 0, behavior: "auto" });
        });
      });
    } else {
      // Only navigate to Overview when the server confirms there is no next
      // exercise — never on a failed/uncertain status fetch.
      $currentPage = "AssessmentOverview";
    }
  }

  // Retry only the advance step (status fetch) after a transient failure, without
  // re-uploading the already-saved result.
  function retryGoToNextExercise() {
    submitErrorMessage = "";
    lastErrorPhase = "upload";
    goToNextExercise();
  }

  function startRecognition() {
    console.log("startRecognition stopTextToSpeech");
    if (ttsInstance) {
      ttsInstance.stopTextToSpeech();
    }

    // DIAGNOSTIC: Log token and SDK state when recognition starts
    console.log("[STT-DIAG] startRecognition called", {
      ttsButtonState,
      hasToken: !!ttsToken,
      tokenLength: ttsToken?.length,
      hasSpeechSDK: !!window.SpeechSDK,
      hasRecognizer: !!recognizer,
      exerciseId,
      instanceId,
      exerciseType: exerciseDetail?.exerciseType,
      durationLimit: exerciseDetail?.durationLimit,
      prepareLimit: exerciseDetail?.prepareLimit,
      timestamp: new Date().toISOString(),
    });

    console.log("startRecognition");

    // No speech token (fetch failed or still pending): starting would reach
    // fromMic() without credentials. Keep the failure/skip state instead; the
    // REFRESH button retries the token.
    if (!ttsToken) {
      console.warn("startRecognition: no speech token, not starting");
      ttsButtonState = "error";
      canRecordNoAudio = true;
      noAudioReason = noAudioReason || "TOKEN_FAILURE";
      submitErrorMessage = $_("MICROPHONE_ERROR");
      return;
    }
    
    // Reset submission state for new recording
    isSubmitting = false;
    isProcessing = false;
    submitErrorMessage = "";
    canRecordNoAudio = false;
    noAudioReason = "";
    speechEndDetectedFired = false;
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
    
    $recognitionResultsFinal = [];
    recognitionResults.push("START");
    recognitionResults = recognitionResults;

    // Safeguard: ensure durationLimit is a valid positive number
    const duration = exerciseDetail.durationLimit;
    if (!duration || duration <= 0) {
      console.warn("Invalid durationLimit, using default of 60 seconds");
      $sharedTimer = 60;
    } else {
      $sharedTimer = duration;
    }
    $sharedTimerMode = "RECORDING";

    // Only this recording's file may be picked up when it stops.
    $lastRecording = undefined;
    recordingStartTime = Date.now();
    fromMic();
    speechToTextOpus.startRecording();
  }

  function handleStopClick() {
    const elapsed = recordingStartTime ? (Date.now() - recordingStartTime) / 1000 : 999;
    if (elapsed < 5 && !showEarlyStopWarning) {
      showEarlyStopWarning = true;
    } else {
      showEarlyStopWarning = false;
      stopRecognition();
    }
  }

  async function stopRecognitionTimeout() {
    console.log("stopRecognitionTimeout");
    
    // DIAGNOSTIC: Log state at timeout stop
    console.log("[STT-DIAG] stopRecognitionTimeout called", {
      ttsButtonState,
      isSubmitting,
      hasRecognizer: !!recognizer,
      recognitionResultsFinalCount: $recognitionResultsFinal?.length,
      speechEndDetectedFired,
      hasFallbackTimer: fallbackTimer !== null,
      exerciseId,
      timestamp: new Date().toISOString(),
    });

    // Lock the submit lifecycle and block the parallel stop path. A manual STOP
    // runs stopRecognition(), which calls stopRecording() and synchronously
    // re-dispatches recordingStop -> stopRecognitionTimeout(); first caller wins.
    if (isProcessing) {
      console.warn("stopRecognitionTimeout: Already processing, ignoring duplicate call");
      return;
    }
    isProcessing = true;

    // Guard against duplicate calls
    if (isSubmitting) {
      console.warn("stopRecognitionTimeout: Already submitting, ignoring duplicate call");
      return;
    }

    ttsButtonState = "timeout";
    recognitionResults.push("TIMEOUT");
    recognitionResults.push("STOP");

    // Clear any existing fallback timer before creating a new one
    if (fallbackTimer !== null) {
      console.warn("stopRecognitionTimeout: Clearing existing fallback timer");
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
    
    // Reset the flag for this new stop operation
    speechEndDetectedFired = false;

    // Fallback timeout: if speechEndDetected doesn't fire within 10 seconds, proceed anyway
    const SPEECH_SDK_TIMEOUT = 10000;
    
    fallbackTimer = setTimeout(() => {
      if (!speechEndDetectedFired && !isSubmitting) {
        console.warn("speechEndDetected timeout - proceeding with submit");
        speechEndDetectedFired = true;
        fallbackTimer = null;
        recognitionResults = recognitionResults;
        formatResult($recognitionResultsFinal);
        if (!soundEffect?.playing()) {
          soundEffect?.play();
        }
        ttsButtonState = "done";
        recognitionResults.push("DONE-FALLBACK");
        submitResult();
      }
    }, SPEECH_SDK_TIMEOUT);

    try {
      if (recognizer) {
        recognizer.speechEndDetected = () => {
          if (speechEndDetectedFired || isSubmitting) {
            console.warn("speechEndDetected: Already handled, ignoring");
            return;
          }
          speechEndDetectedFired = true;
          if (fallbackTimer !== null) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
          }
          console.log(`speechEndDetected`);
          recognitionResults = recognitionResults;
          formatResult($recognitionResultsFinal);
          if (!soundEffect?.playing()) {
            soundEffect?.play();
          }
          ttsButtonState = "done";
          recognitionResults.push("DONE");

          submitResult();
        };
        recognizer.stopContinuousRecognitionAsync();
      } else {
        speechEndDetectedFired = true;
        if (fallbackTimer !== null) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        recognitionResults = recognitionResults;
        formatResult($recognitionResultsFinal);
        if (!soundEffect?.playing()) {
          soundEffect?.play();
        }
        ttsButtonState = "done";
        recognitionResults.push("DONE");
        submitResult();
      }
    } catch (error) {
      speechEndDetectedFired = true;
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      console.error("stopRecognitionTimeout error - TODO", error);
      submitResult();
    }
  }

  async function stopRecognition(fromTimer = false) {
    console.log("stopRecognition");

    // DIAGNOSTIC: Log state at manual stop
    console.log("[STT-DIAG] stopRecognition called", {
      fromTimer,
      ttsButtonState,
      isSubmitting,
      hasRecognizer: !!recognizer,
      recognitionResultsFinalCount: $recognitionResultsFinal?.length,
      speechEndDetectedFired,
      hasFallbackTimer: fallbackTimer !== null,
      willSkipRecognizerStop: !recognizer || ttsButtonState === "wait",
      exerciseId,
      timestamp: new Date().toISOString(),
    });

    // Lock the submit lifecycle as soon as the user commits to stopping. This
    // also short-circuits the re-entrant stopRecognitionTimeout() triggered via
    // stopRecording() -> recordingStop, so the stop runs through one path only.
    if (isProcessing) {
      console.warn("stopRecognition: Already processing, ignoring duplicate call");
      return;
    }
    isProcessing = true;

    // Guard against duplicate calls
    if (isSubmitting) {
      console.warn("stopRecognition: Already submitting, ignoring duplicate call");
      return;
    }

    recognitionResults.push("STOP");
    
    // Clear any existing fallback timer before creating a new one
    if (fallbackTimer !== null) {
      console.warn("stopRecognition: Clearing existing fallback timer");
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
    
    // Reset the flag for this new stop operation
    speechEndDetectedFired = false;
    
    // Fallback timeout: if speechEndDetected doesn't fire within 10 seconds, proceed anyway
    const SPEECH_SDK_TIMEOUT = 10000;
    
    fallbackTimer = setTimeout(() => {
      if (!speechEndDetectedFired && !isSubmitting) {
        console.warn("stopRecognition speechEndDetected timeout - proceeding with submit");
        speechEndDetectedFired = true;
        fallbackTimer = null;
        recognitionResults = recognitionResults;
        formatResult($recognitionResultsFinal);
        if (!soundEffect?.playing()) {
          soundEffect?.play();
        }
        ttsButtonState = "done";
        recognitionResults.push("DONE-FALLBACK");
        submitResult();
      }
    }, SPEECH_SDK_TIMEOUT);
    
    //await new Promise((r) => setTimeout(r, 2000));
    if (recognizer && ttsButtonState !== "wait") {
      ttsButtonState = "stop";
      speechToTextOpus.stopRecording();
      recognizer.speechEndDetected = () => {
        if (speechEndDetectedFired || isSubmitting) {
          console.warn("speechEndDetected: Already handled, ignoring");
          return;
        }
        speechEndDetectedFired = true;
        if (fallbackTimer !== null) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        console.log(`speechEndDetected`);
        recognitionResults = recognitionResults;
        formatResult($recognitionResultsFinal);
        if (!soundEffect?.playing()) {
          soundEffect?.play();
        }
        ttsButtonState = "done";
        recognitionResults.push("DONE");

        submitResult();
      };
      recognizer.stopContinuousRecognitionAsync();
    } else {
      speechEndDetectedFired = true;
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      ttsButtonState = "stop";
      speechToTextOpus.stopRecording();
      recognitionResults = recognitionResults;
      formatResult($recognitionResultsFinal);
      if (!soundEffect?.playing()) {
        soundEffect?.play();
      }
      ttsButtonState = "done";
      recognitionResults.push("DONE");
      submitResult();
    }
  }

  async function stopFromTimerOnly(fromTimer = false) {
    console.log("stopFromTimerOnly", { fromTimer });

    // Block re-entry. This is called from the on-screen CONTINUE/START button,
    // a possible double-click, and the IntroTimer's counterStop event when it
    // reaches zero. First caller wins; the rest no-op so we never fire two
    // parallel submits or submit after the upload has already started.
    if (isProcessing) {
      console.warn("stopFromTimerOnly: Already processing, ignoring duplicate call");
      return;
    }

    isProcessing = true;

    // Freeze the visible countdown so it cannot reach zero and dispatch a second
    // submit while this upload is in flight.
    introTimerComponent?.pauseTimer();

    // Timer expiry can fire while a confirmation dialog is open — the submit
    // proceeds, so neither dialog may linger over the spinner. The blank-answer
    // warning goes with them: the answer is on its way, incomplete or not.
    showUnderWordCountModal = false;
    showIncompleteGapModal = false;
    showAnswerWarning = false;

    ttsButtonState = "stop";
    recognitionResults.push("STOP");

    submitResultSkip();
  }

  function submitRetry() {
    // Determine if we need to refresh token or retry submission
    // If there's no recording, the error was likely during token fetch/STT init
    // If there's a recording, the error was during submission
    submitErrorMessage = "";
    // The last failure was advancing to the next exercise, not the upload. The
    // result is already saved — just re-attempt the status fetch.
    if (lastErrorPhase === "advance") {
      console.log("submitRetry: advance phase, retrying goToNextExercise");
      retryGoToNextExercise();
      return;
    }
    // The last sentence of a series is already kept (with or without a
    // recording): only the upload is retried.
    if (isSeries && seriesTakes[sentenceIndex] !== undefined) {
      ttsButtonState = "done";
      submitResult();
      return;
    }
    if (!$lastRecording) {
      console.log("submitRetry: No recording found, refreshing token for STT");
      refreshToken();
    } else {
      console.log("submitRetry: Recording found, retrying submission");
      ttsButtonState = "done";
      submitResult();
    }
  }

  // Explicit retry for the non-speaking (CONTINUE/START) path. The previous
  // upload failed and reset the lock; this re-acquires it and resubmits the
  // already-collected answer without re-pausing the (already frozen) timer.
  function retrySubmitSkip() {
    console.log("retrySubmitSkip");
    submitErrorMessage = "";
    // If the failure was advancing (status fetch) rather than the upload, the
    // answer is already saved — just re-attempt the advance.
    if (lastErrorPhase === "advance") {
      retryGoToNextExercise();
      return;
    }
    // No completeness check: the answer being retried was already accepted for
    // submission (confirmed by the student, or forced by timer expiry), and
    // re-asking after an upload failure would strand it.
    isProcessing = true;
    submitResultSkip();
  }

  // CONTINUE handler for the answer-then-submit exercise types. Both soft
  // confirmations live here — blanks left on FILL_THE_GAP, FILL_THE_LETTERS
  // and DRAG_WORDS, and the WRITING word-count minimum. Timer expiry bypasses
  // both: counterStop calls stopFromTimerOnly directly and submits whatever
  // was answered.
  function onContinueClick() {
    if (isProcessing) return;
    // Blank gaps warn and ask rather than block: the student is asked to
    // confirm, then decides whether to go back or submit as is.
    if (
      exerciseDetail?.exerciseType === "FILL_THE_GAP" &&
      unansweredGapCount > 0
    ) {
      showAnswerWarning = true;
      showIncompleteGapModal = true;
      return;
    }
    // Empty letter boxes and word slots: the same warning and confirmation.
    if (unansweredLetterCount > 0 || emptyDragSlotCount > 0) {
      showAnswerWarning = true;
      showIncompleteGapModal = true;
      return;
    }
    if (!isNextAllowed) {
      showAnswerWarning = true;
      return;
    }
    showAnswerWarning = false;
    if (
      exerciseDetail?.exerciseType === "WRITING" &&
      writingMinWords > 0 &&
      writingWordCount < writingMinWords
    ) {
      showUnderWordCountModal = true;
      return;
    }
    stopFromTimerOnly();
  }

  function confirmUnderWordCountSubmit() {
    showUnderWordCountModal = false;
    stopFromTimerOnly();
  }

  function confirmIncompleteGapSubmit() {
    showIncompleteGapModal = false;
    showAnswerWarning = false;
    stopFromTimerOnly();
  }

  // Record an explicit "no audio" result for a speaking exercise the student
  // could not (or chose not to) complete: mic denied, token/recognition failure,
  // or no speech detected. Guarantees a result row exists (scored a deterministic
  // 0 by the backend/scoring service via the NO_AUDIO marker) instead of leaving
  // the exercise silently missing. Reuses the submitResultSkip upload shape but
  // builds a fixed marker payload rather than the formatResult aggregate (which
  // is NaN with no speech). If a recording was captured (e.g. empty recognition),
  // its audio is still uploaded for later review.
  function submitNoAudioResult(reason) {
    console.log("[STT-DIAG] submitNoAudioResult called", {
      reason,
      isSubmitting,
      isProcessing,
      exerciseId,
      instanceId,
      exerciseType: exerciseDetail?.exerciseType,
      hasRecording: !!$lastRecording?.blob,
      timestamp: new Date().toISOString(),
    });

    if (isSubmitting) {
      console.warn("submitNoAudioResult: Already submitting, ignoring duplicate call");
      return;
    }

    // In a LISTEN_REPEAT series only this sentence is lost: it is kept as
    // NO_AUDIO and the series goes on.
    if (isSeries) {
      try {
        if (recognizer) {
          recognizer.stopContinuousRecognitionAsync(() => {}, () => {});
        }
      } catch (_) {
        /* best effort */
      }
      // Stop local capture and its countdown too, without the recordingStop
      // that would submit the next sentence once it has started.
      speechToTextOpus?.cancelRecording();
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      finishSeriesSentence([], reason);
      return;
    }
    isSubmitting = true;
    isProcessing = true;
    canRecordNoAudio = false;

    // Stop any in-flight recognition so it cannot fire a late, competing submit.
    try {
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync(() => {}, () => {});
      }
    } catch (_) {
      /* best effort */
    }
    speechToTextOpus?.cancelRecording();
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }

    // Deterministic NO_AUDIO marker — canonical contract shared with the backend
    // (stored verbatim) and the scoring service (recognized + scored as 0).
    aggregateScore = {
      status: "NO_AUDIO",
      noAudioReason: reason,
      pronScore: 0,
      accuracyScore: 0,
      fluencyScore: 0,
      completenessScore: 0,
      confidence: 0,
      wordCount: 0,
      timerRemaining: $sharedTimer,
      // Same rule as submitResult: a prompt recording heard to the end before
      // capture failed still counts as finished.
      audioFinished: audioStatus === "ENDED",
    };

    const hasRecording = !!$lastRecording?.blob;
    const uploads = [
      uploadExerciseResults(instanceId, exerciseId, aggregateScore, []),
    ];
    if (hasRecording) {
      // Preserve whatever audio was captured (empty-recognition case).
      uploads.unshift(uploadMedia(instanceId, exerciseId, $lastRecording.blob));
    }

    const UPLOAD_TIMEOUT = hasRecording ? 60000 : 30000;

    const uploadPromise = Promise.race([
      Promise.all(uploads),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Upload timeout")), UPLOAD_TIMEOUT),
      ),
    ]);

    uploadPromise
      .then(() => {
        console.log("[STT-DIAG] NO_AUDIO result recorded", {
          reason,
          exerciseId,
          instanceId,
        });
        isSubmitting = false;
        submitErrorMessage = "";
        goToNextExercise();
      })
      .catch((e) => {
        lastErrorPhase = "upload";
        canRecordNoAudio = true; // still a speech path: allow another skip attempt
        trackError("submitNoAudioResult upload failed", e, {
          step: "no-audio",
          noAudioReason: reason,
          exerciseType: exerciseDetail?.exerciseType,
        });
        isSubmitting = false;
        isProcessing = false;
        ttsButtonState = "error";
        submitErrorMessage = $_("UPLOAD_FAILED_RETRY");
      });
  }

  function submitResult() {
    console.log("submitResults");

    // A LISTEN_REPEAT series keeps each sentence and submits once, after the
    // last one.
    if (isSeries) {
      finishSeriesSentence($recognitionResultsFinal || [], "EMPTY_RECOGNITION");
      return;
    }

    // If recognition produced nothing (no speech detected / recognizer failed)
    // for a speaking exercise, record an explicit NO_AUDIO result instead of a
    // NaN-filled aggregate that scores unpredictably and looks "skipped".
    const isSpeakingType = ["READ", "QUESTION", "IMAGE", "LISTEN", "LISTEN_REPEAT"].includes(
      exerciseDetail?.exerciseType,
    );
    if (
      !isSubmitting &&
      isSpeakingType &&
      (!$recognitionResultsFinal || $recognitionResultsFinal.length === 0)
    ) {
      console.warn("[STT-DIAG] submitResult: empty recognition -> NO_AUDIO", {
        exerciseId,
        instanceId,
        exerciseType: exerciseDetail?.exerciseType,
      });
      submitNoAudioResult("EMPTY_RECOGNITION");
      return;
    }

    // DIAGNOSTIC: Log full state at submission time
    console.log("[STT-DIAG] submitResult called", {
      isSubmitting,
      ttsButtonState,
      recognitionResultsFinalCount: $recognitionResultsFinal?.length,
      recognitionResultsFinalEmpty: $recognitionResultsFinal?.length === 0,
      hasLastRecording: !!$lastRecording,
      lastRecordingBlobSize: $lastRecording?.blob?.size,
      aggregateScoreKeys: aggregateScore ? Object.keys(aggregateScore) : [],
      aggregateScorePronScore: aggregateScore?.pronScore,
      aggregateScoreWordCount: aggregateScore?.wordCount,
      exerciseType: exerciseDetail?.exerciseType,
      exerciseId,
      instanceId,
      timestamp: new Date().toISOString(),
    });

    // Guard against duplicate submissions
    if (isSubmitting) {
      console.warn("submitResult: Already submitting, ignoring duplicate call");
      return;
    }
    
    isSubmitting = true;

    let audioFinished = undefined;

    if (audioStatus) {
      if (audioStatus === "ENDED") {
        audioFinished = true;
      } else {
        audioFinished = false;
      }
    }

    aggregateScore["timerRemaining"] = $sharedTimer;
    aggregateScore["audioFinished"] = audioFinished;

    if (
      exerciseDetail.exerciseType === "INTRO" ||
      exerciseDetail.exerciseType === "PHOTO_VERIFICATION"
    ) {
      if (photoVerification) {
        // console.log(555, photoVerification.fileNames);
        aggregateScore["photoFileNames"] = photoVerification.fileNames;
        aggregateScore["photoFileUrls"] = photoVerification.fileUrls;
        aggregateScore["initialPhoto"] = $initialPhoto;
      }
      if (photoVerificationOutcome) {
        aggregateScore["photoVerificationOutcome"] = photoVerificationOutcome;
      }
    } else if (
      exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" ||
      exerciseDetail.exerciseType === "MULTICHOICE_SELECT"
    ) {
      aggregateScore["selectedOption"] = selectedOption;
      aggregateScore["selectedAnswerCorrect"] =
        selectedOption === exerciseDetailData?.correctOption;
    } else if (exerciseDetail.exerciseType === "MULTICHOICE_COMBINED") {
      let correctCount = 0;
      aggregateScore["selectedOptionList"] = selectedOptionList.map((v, i) => {
        if (v === exerciseDetailData?.questions[i]?.correctOption) {
          correctCount++;
        }
        return {
          selectedOption: v,
          correctOption: v === exerciseDetailData?.questions[i]?.correctOption,
        };
      });
      aggregateScore["selectedAnswerCorrectCount"] = correctCount;
      aggregateScore["questionCount"] = exerciseDetailData?.questions?.length;
    } else if (exerciseDetail.exerciseType === "FILL_THE_GAP") {
      // Evaluate each gap client-side (definition regex vs trimmed student
      // input) so the result is final before upload — the scoring service only
      // reads the precomputed counts, same contract as MULTICHOICE_COMBINED.
      let correctCount = 0;
      const gaps = exerciseDetailData?.gaps || [];
      aggregateScore["gapAnswerList"] = gaps.map((g, i) => {
        const answerText = (gapAnswers[i] ?? "").trim();
        const correct = evaluateGapAnswer(g?.answer, answerText);
        if (correct) {
          correctCount++;
        }
        return { answer: answerText, correct: correct, fieldType: g?.fieldType };
      });
      aggregateScore["selectedAnswerCorrectCount"] = correctCount;
      aggregateScore["questionCount"] = gaps.length;
    } else if (exerciseDetail.exerciseType === "FILL_THE_LETTERS") {
      // Scored client-side like FILL_THE_GAP: letterAnswerList plus the
      // selectedAnswerCorrectCount / questionCount the scoring service reads.
      Object.assign(
        aggregateScore,
        scoreFillTheLetters(letterExercise?.blocks || [], letterAnswers, {
          caseSensitive: !!exerciseDetailData?.caseSensitive,
        }),
      );
    } else if (exerciseDetail.exerciseType === "DRAG_WORDS") {
      Object.assign(
        aggregateScore,
        scoreDragWords(
          dragExercise?.slots || [],
          dragExercise?.words || [],
          dragPlacements,
        ),
      );
    } else if (exerciseDetail.exerciseType === "WRITING") {
      aggregateScore["writingAnswer"] = writingText;
      aggregateScore["wordCount"] = writingWordCount;
      aggregateScore["minWords"] = writingMinWords;
      aggregateScore["allowCopyPaste"] = writingAllowCopyPaste;
      aggregateScore["securitySummary"] = summarizeWritingLog(writingTrackingLog);
    }

    // if (
    //   exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" ||
    //   exerciseDetail.exerciseType === "MULTICHOICE_SELECT" ||
    //   exerciseDetail.exerciseType === "MULTICHOICE_COMBINED"
    // ) {
    //   let audioFinished = undefined;

    //   if (audioPlayer && audioPlayer.readyState >= 2) {
    //     if (audioPlayer?.currentTime >= audioPlayer?.duration) {
    //       audioFinished = true;
    //     } else {
    //       audioFinished = false;
    //     }
    //   }

    //   aggregateScore = {
    //     selectedOption: selectedOption,
    //     selectedAnswerCorrect:
    //       selectedOption === exerciseDetailData?.correctOption,
    //     timerRemaining: $sharedTimer,
    //     audioFinished: audioFinished,
    //   };
    // }

    // Add timeout to prevent infinite hanging
    const UPLOAD_TIMEOUT = 60000; // 60 seconds
    
    const uploadPromises = Promise.race([
      Promise.all([
        uploadMedia(instanceId, exerciseId, $lastRecording?.blob),
        uploadExerciseResults(
          instanceId,
          exerciseId,
          aggregateScore,
          $recognitionResultsFinal,
        ),
      ]),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout - please check your connection')), UPLOAD_TIMEOUT)
      )
    ]);

    uploadPromises
      .then(() => {
        console.log("Successfully sent data");
        isSubmitting = false;
        submitErrorMessage = "";
        //router.goto("/assessment/" + instanceId);
        goToNextExercise();
      })
      .catch((e) => {
        lastErrorPhase = "upload";
        trackError("submitResult upload failed", e, {
          submitPath: "speaking",
          timerRemaining: $sharedTimer,
        });
        isSubmitting = false; // Reset flag to allow retry
        isProcessing = false; // Release UI lock so REFRESH is actionable
        ttsButtonState = "error";
        // Keep the recording and stay on this exercise. The REFRESH button
        // (submitRetry) resubmits the preserved recording. We intentionally do
        // NOT auto-advance or navigate to Overview on a failed save, because
        // that is what made exercises appear "skipped" with no stored result.
        submitErrorMessage = $_("UPLOAD_FAILED_RETRY");
      });
  }

  function submitResultSkip() {
    console.log("submitResultSkip");

    // Guard against duplicate submissions
    if (isSubmitting) {
      console.warn("submitResultSkip: Already submitting, ignoring duplicate call");
      return;
    }
    
    isSubmitting = true;

    let audioFinished = undefined;

    if (audioPlayer && audioPlayer.readyState >= 2) {
      if (audioPlayer?.currentTime >= audioPlayer?.duration) {
        audioFinished = true;
      } else {
        audioFinished = false;
      }
    }

    aggregateScore["timerRemaining"] = $sharedTimer;
    aggregateScore["audioFinished"] = audioFinished;

    if (
      exerciseDetail.exerciseType === "INTRO" ||
      exerciseDetail.exerciseType === "PHOTO_VERIFICATION"
    ) {
      if (photoVerification) {
        aggregateScore["photoFileNames"] = photoVerification.fileNames;
        aggregateScore["photoFileUrls"] = photoVerification.fileUrls;
        aggregateScore["initialPhoto"] = $initialPhoto;
      }
      // Why the step ended, so an auto-skipped verification is auditable
      // instead of arriving as an indistinguishable empty photo list.
      if (photoVerificationOutcome) {
        aggregateScore["photoVerificationOutcome"] = photoVerificationOutcome;
      }
    } else if (
      exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" ||
      exerciseDetail.exerciseType === "MULTICHOICE_SELECT"
    ) {
      aggregateScore["selectedOption"] = selectedOption;
      aggregateScore["selectedAnswerCorrect"] =
        selectedOption === exerciseDetailData?.correctOption;
    } else if (exerciseDetail.exerciseType === "MULTICHOICE_COMBINED") {
      let correctCount = 0;
      aggregateScore["selectedOptionList"] = selectedOptionList.map((v, i) => {
        if (v === exerciseDetailData?.questions[i]?.correctOption) {
          correctCount++;
        }
        return {
          selectedOption: v,
          correctOption: v === exerciseDetailData?.questions[i]?.correctOption,
        };
      });
      aggregateScore["selectedAnswerCorrectCount"] = correctCount;
      aggregateScore["questionCount"] = exerciseDetailData?.questions?.length;
    } else if (exerciseDetail.exerciseType === "FILL_THE_GAP") {
      // Evaluate each gap client-side (definition regex vs trimmed student
      // input) so the result is final before upload — the scoring service only
      // reads the precomputed counts, same contract as MULTICHOICE_COMBINED.
      let correctCount = 0;
      const gaps = exerciseDetailData?.gaps || [];
      aggregateScore["gapAnswerList"] = gaps.map((g, i) => {
        const answerText = (gapAnswers[i] ?? "").trim();
        const correct = evaluateGapAnswer(g?.answer, answerText);
        if (correct) {
          correctCount++;
        }
        return { answer: answerText, correct: correct, fieldType: g?.fieldType };
      });
      aggregateScore["selectedAnswerCorrectCount"] = correctCount;
      aggregateScore["questionCount"] = gaps.length;
    } else if (exerciseDetail.exerciseType === "FILL_THE_LETTERS") {
      // Scored client-side like FILL_THE_GAP: letterAnswerList plus the
      // selectedAnswerCorrectCount / questionCount the scoring service reads.
      Object.assign(
        aggregateScore,
        scoreFillTheLetters(letterExercise?.blocks || [], letterAnswers, {
          caseSensitive: !!exerciseDetailData?.caseSensitive,
        }),
      );
    } else if (exerciseDetail.exerciseType === "DRAG_WORDS") {
      Object.assign(
        aggregateScore,
        scoreDragWords(
          dragExercise?.slots || [],
          dragExercise?.words || [],
          dragPlacements,
        ),
      );
    } else if (exerciseDetail.exerciseType === "WRITING") {
      aggregateScore["writingAnswer"] = writingText;
      aggregateScore["wordCount"] = writingWordCount;
      aggregateScore["minWords"] = writingMinWords;
      aggregateScore["allowCopyPaste"] = writingAllowCopyPaste;
      aggregateScore["securitySummary"] = summarizeWritingLog(writingTrackingLog);
    }

    // Add timeout to prevent infinite hanging
    const UPLOAD_TIMEOUT = 30000; // 30 seconds for skip (no media upload)

    // WRITING uploads its full edit history + security events as the raw input
    // data (the channel speech exercises use for recognition results).
    const rawResultData =
      exerciseDetail.exerciseType === "WRITING" ? writingTrackingLog : [];

    const uploadPromise = Promise.race([
      Promise.all([
        uploadExerciseResults(instanceId, exerciseId, aggregateScore, rawResultData),
      ]),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout')), UPLOAD_TIMEOUT)
      )
    ]);

    uploadPromise
      .then(() => {
        console.log("Successfully sent data");
        isSubmitting = false;
        submitErrorMessage = "";
        //router.goto("/assessment/" + instanceId);
        goToNextExercise();
      })
      .catch((e) => {
        lastErrorPhase = "upload";
        trackError("submitResultSkip upload failed", e, {
          submitPath: "skip",
          exerciseType: exerciseDetail?.exerciseType,
        });
        isSubmitting = false; // Reset flag so the answer can be re-submitted
        isProcessing = false; // Release UI lock so the RETRY button is actionable
        // Stay on this exercise and surface a retry message. The RETRY button
        // (retrySubmitSkip) resubmits the collected answer. We no longer
        // auto-navigate to Overview, which abandoned the answer and made the
        // exercise look skipped.
        submitErrorMessage = $_("UPLOAD_FAILED_RETRY");
      });
  }

  function fromMic() {
    let speechConfig;

    // A LISTEN_REPEAT series checks each recording against its own sentence.
    let textToRead = referenceText(
      exerciseDetail?.exerciseType,
      exerciseDetailData,
      sentenceIndex,
    );

    // DIAGNOSTIC: Log token state when entering fromMic
    console.log("[STT-DIAG] fromMic called", {
      hasToken: !!ttsToken,
      tokenLength: ttsToken?.length,
      tokenFirstChars: ttsToken?.substring(0, 10) + '...',
      ttsButtonState,
      textToReadLength: textToRead?.length,
      exerciseLanguage: exerciseDetail?.language,
      exerciseId,
      timestamp: new Date().toISOString(),
    });

    if (ttsToken) {
      speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        ttsToken,
        ttsRegion,
      );
    } else {
      if (ttsToken === "" || ttsToken === "subscription") {
        alert(
          "Please enter your Microsoft Cognitive Services Speech subscription key!",
        );
        return;
      }
      speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        subscriptionKey.value,
        serviceRegion.value,
      );
    }

    speechConfig.speechRecognitionLanguage = exerciseDetail?.language
      ? exerciseDetail.language
      : "en-US";
    //speechConfig.setProfanity(2);

    var pronunciationAssessmentConfig =
      new SpeechSDK.PronunciationAssessmentConfig(
        textToRead,
        SpeechSDK.PronunciationAssessmentGradingSystem.HundredMark,
        SpeechSDK.PronunciationAssessmentGranularity.Phoneme,
        true,
      );

    let audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

    pronunciationAssessmentConfig.applyTo(recognizer);

    console.log("Speak into your microphone.");

    recognizer.recognizing = (s, e) => {
      console.log(`RECOGNIZING: Text=${e.result.text}`);
    };

    recognizer.speechStartDetected = () => {
      console.log(`speechStartDetected`);
      ttsButtonState = "recording";
      //recognitionResults.push({ speechStartDetected: e?.result });
    };

    recognizer.speechEndDetected = () => {
      console.log(`speechEndDetected`);
      //recognitionResults.push({ speechEndDetected: e?.result });
    };

    recognizer.recognized = (s, e) => {
      if (e.result.reason == SpeechSDK.ResultReason.RecognizedSpeech) {
        console.log(`RECOGNIZED: Text=${e.result.text}`);
        // DIAGNOSTIC: Log each recognized result
        console.log("[STT-DIAG] recognized result", {
          text: e.result.text,
          resultCount: $recognitionResultsFinal.length + 1,
          timestamp: new Date().toISOString(),
        });
        recognitionResults.push({ recognized: e?.result });

        var pronunciationAssessmentResult = JSON.parse(e.result.json);
        recognitionResults.push({
          pronunciationAssessmentResult: pronunciationAssessmentResult,
        });
        recognitionResults = recognitionResults;

        $recognitionResultsFinal.push(pronunciationAssessmentResult);
        $recognitionResultsFinal = $recognitionResultsFinal;
      } else if (e.result.reason == SpeechSDK.ResultReason.NoMatch) {
        // DIAGNOSTIC: Log NoMatch with details
        console.warn("[STT-DIAG] NOMATCH: Speech could not be recognized", {
          reason: e.result.reason,
          noMatchReason: e.result?.properties?.getProperty?.("NoMatchReason"),
          resultId: e.result?.resultId,
          duration: e.result?.duration,
          exerciseId,
          timestamp: new Date().toISOString(),
        });
        console.log("NOMATCH: Speech could not be recognized.");
      }
    };

    recognizer.canceled = (s, e) => {
      // DIAGNOSTIC: Log ALL cancellation details including result count at time of cancel
      console.error("[STT-DIAG] Speech recognition CANCELED:", {
        errorCode: e.errorCode,
        errorDetails: e.errorDetails,
        reason: e.reason,
        reasonName: e.reason === 0 ? 'EndOfStream' : e.reason === 1 ? 'Error' : e.reason === 5 ? 'AuthFailure' : 'Unknown(' + e.reason + ')',
        currentTtsButtonState: ttsButtonState,
        recognitionResultsFinalCount: $recognitionResultsFinal?.length,
        hasRecognizer: !!recognizer,
        exerciseId,
        instanceId,
        timestamp: new Date().toISOString(),
      });
      // Enhanced error logging for TrackJS
      console.error("Speech recognition CANCELED:", {
        errorCode: e.errorCode,
        errorDetails: e.errorDetails,
        reason: e.reason,
        exerciseId,
        instanceId,
        timestamp: new Date().toISOString(),
      });
      
      // Check for authentication failure (token expired)
      // CancellationReason.AuthenticationFailure = 5 in the SDK
      const isAuthFailure = e.reason === 5 || 
        (e.errorDetails && e.errorDetails.toLowerCase().includes('auth')) ||
        (e.errorDetails && e.errorDetails.toLowerCase().includes('token'));
      
      if (isAuthFailure) {
        console.error("Authentication failure detected - token may have expired");
        ttsButtonState = "error";
        console.error("error with SpeechSDK");
        // User can click the Refresh button to get a new token, or skip (which
        // records an explicit NO_AUDIO result) if the token never recovers.
        canRecordNoAudio = true;
        noAudioReason = "AUTH_FAILURE";
        submitErrorMessage = $_("MICROPHONE_ERROR");
        trackError(
          "speech recognition canceled (auth)",
          new Error(e.errorDetails || "auth failure"),
          { step: "recognition", noAudioReason: "AUTH_FAILURE" },
        );
      } else if (e.errorCode !== 0) {
        // Other errors - set error state
        ttsButtonState = "error";
        console.error("error with SpeechSDK");
        canRecordNoAudio = true;
        noAudioReason = "SPEECH_CANCELED";
        submitErrorMessage = $_("MICROPHONE_ERROR");
        trackError(
          "speech recognition canceled",
          new Error(e.errorDetails || "errorCode " + e.errorCode),
          { step: "recognition", noAudioReason: "SPEECH_CANCELED" },
        );
      }

      recognizer.stopContinuousRecognitionAsync();
    };

    ttsButtonState = "wait";
    // DIAGNOSTIC: Log recognizer setup completion
    console.log("[STT-DIAG] fromMic - recognizer created, starting continuous recognition", {
      language: speechConfig?.speechRecognitionLanguage || exerciseDetail?.language,
      hasRecognizer: !!recognizer,
      hasPronunciationConfig: !!pronunciationAssessmentConfig,
      textToReadLength: textToRead?.length,
      timestamp: new Date().toISOString(),
    });
    recognizer.startContinuousRecognitionAsync(
      () => {
        console.log("[STT-DIAG] startContinuousRecognitionAsync succeeded");
      },
      (err) => {
        console.error("[STT-DIAG] startContinuousRecognitionAsync FAILED", {
          error: err?.message || err,
          exerciseId,
          instanceId,
          timestamp: new Date().toISOString(),
        });
      }
    );
  }

  function formatResult(r) {
    console.log("results", r);
    // DIAGNOSTIC: Log input to formatResult
    console.log("[STT-DIAG] formatResult called", {
      inputLength: r?.length,
      isEmpty: !r || r.length === 0,
      exerciseId,
      ttsButtonState,
      timestamp: new Date().toISOString(),
    });
    if (!r || r.length === 0) {
      console.warn("[STT-DIAG] formatResult called with EMPTY results - STT data will be missing", {
        exerciseId,
        instanceId,
        exerciseType: exerciseDetail?.exerciseType,
        ttsButtonState,
      });
    }
    const sum = (arr) => arr.reduce((p, c) => p + c, 0);
    const average = (arr) => arr.reduce((p, c) => p + c, 0) / arr.length;

    parts = r.flatMap((part) => {
      if (part?.NBest.length > 0) {
        if (part.NBest[0].PronunciationAssessment) {
          return {
            recognitionStatus: part.RecognitionStatus,
            offset: part.Offset,
            duration: part.Duration,
            displayText: part.DisplayText,
            confidence: part.NBest[0].Confidence,
            accuracyScore: part.NBest[0].PronunciationAssessment?.AccuracyScore,
            fluencyScore: part.NBest[0].PronunciationAssessment?.FluencyScore,
            completenessScore:
              part.NBest[0].PronunciationAssessment?.CompletenessScore,
            pronScore: part.NBest[0].PronunciationAssessment?.PronScore,
            words: part.NBest[0].Words,
            wordCount: part.NBest[0].Words?.length
              ? part.NBest[0].Words?.length
              : 0,
          };
        } else {
          return {
            recognitionStatus: part.RecognitionStatus,
            offset: part.Offset,
            duration: part.Duration,
            displayText: part.DisplayText,
            confidence: part.NBest[0].Confidence,
            words: part.NBest[0].Words,
            wordCount: part.NBest[0].Words?.length
              ? part.NBest[0].Words?.length
              : 0,
          };
        }
      } else {
        let x = {
          recognitionStatus: part.RecognitionStatus,
          offset: part.Offset,
          duration: part.Duration,
          displayText: part.DisplayText,
        };
        return [];
      }
    });

    parts = parts.filter((x) => x.wordCount > 0);

    // console.log("parts", JSON.stringify(parts));

    aggregateScore = {
      start: Math.min(...parts.flatMap((e) => e?.offset)),
      end: Math.max(...parts.flatMap((e) => e?.offset + e?.duration)),

      confidence: average(
        parts.flatMap((e) => (e?.confidence ? e?.confidence : [])),
      ).toFixed(2),
      accuracyScore: average(
        parts.flatMap((e) => (e?.accuracyScore ? e?.accuracyScore : [])),
      ).toFixed(0),
      fluencyScore: average(
        parts.flatMap((e) => (e?.fluencyScore ? e?.fluencyScore : [])),
      ).toFixed(0),
      completenessScore: average(
        parts.flatMap((e) =>
          e?.completenessScore ? e?.completenessScore : [],
        ),
      ).toFixed(0),
      pronScore: average(
        parts.flatMap((e) => (e?.pronScore ? e?.pronScore : [])),
      ).toFixed(0),
      audioDuration: $audioControlDuration,
      duration: undefined,
      wordDurationSum: undefined,
      wordCount: undefined,
      wordPerMinute: undefined,
      wordPerMinuteWordsOnly: undefined,
    };

    aggregateScore.audioDuration = $audioControlDuration;
    aggregateScore.duration = (
      (aggregateScore?.end - aggregateScore?.start) /
      10000000
    ).toFixed(2);
    aggregateScore.wordDurationSum = (
      sum(
        parts
          .flatMap((e) => (e?.words.length ? e.words : []))
          .map((w) => w.Duration),
      ) / 10000000
    ).toFixed(2);

    aggregateScore.wordCount = sum(parts.map((w) => w.wordCount));

    aggregateScore.wordPerMinute = (
      (aggregateScore.wordCount / aggregateScore.duration) *
      60
    ).toFixed(0);

    aggregateScore.wordPerMinuteWordsOnly = (
      (aggregateScore.wordCount / aggregateScore.wordDurationSum) *
      60
    ).toFixed(0);

    aggregateScore = aggregateScore;
    console.log("aggregateScore", JSON.stringify(aggregateScore));
  }

  function selectOption(o) {
    selectedOption = o;

    isNextAllowed = true;
  }

  function selectOptions(i, o) {
    selectedOptionList[i] = o;

    console.log("selectedOptionList", selectedOptionList);

    // isNextAllowed = true;

    if (
      exerciseDetailData?.questions?.length ===
      selectedOptionList?.filter((v) => v)?.length
    ) {
      isNextAllowed = true;
    } else {
      isNextAllowed = false;
    }
  }

 

  let audioStatus = "NOT_STARTED";
  let imageStatus = "NOT_STARTED";

  // ── READ / QUESTION with a recording of the prompt ──
  // READ is TOEFL "Listen & Repeat", QUESTION is a spoken question. Latched
  // once the recording has played to the end or failed to load; it releases
  // the preparation countdown held while the candidate listens.
  let promptAudioDone = false;
  // Index into promptQueue of the recording being waited for: QUESTION can
  // play an introduction before its question recording.
  let promptStep = 0;
  $: promptQueue = promptAudioQueue(
    exerciseDetail?.exerciseType,
    exerciseDetailData,
    sentenceIndex,
  );
  $: promptPlayback = promptPlaybackSettings(exerciseDetailData);
  $: hasPromptAudio = hasAudioPrompt(
    exerciseDetail?.exerciseType,
    exerciseDetailData,
    sentenceIndex,
  );
  $: listenRepeatTextHidden = isListenRepeatTextHidden(
    exerciseDetail?.exerciseType,
    exerciseDetailData,
  );
  $: holdPrepareTimer = shouldHoldPrepareTimer(
    exerciseDetail?.exerciseType,
    exerciseDetailData,
    promptAudioDone,
    sentenceIndex,
  );

  // Moves on to the next recording of the queue; the last one releases the
  // preparation hold. Replays of an earlier recording change nothing.
  function advancePromptQueue(index) {
    if (index !== promptStep) return;
    promptStep = index + 1;
    if (promptStep >= promptQueue.length) {
      promptAudioDone = true;
    }
  }

  function promptAudioEnded(event) {
    audioEnded();
    advancePromptQueue(event?.detail?.index ?? promptStep);
  }

  // A recording that cannot be played must not freeze the countdown: the
  // candidate still gets their preparation time, then the recording starts.
  function promptAudioFailed(event) {
    advancePromptQueue(event?.detail?.index ?? promptStep);
  }

  // ── LISTEN_REPEAT series ──
  // Each sentence is recorded and checked on its own, then kept here until
  // the last one has been answered and the exercise is submitted once.
  let sentenceIndex = 0;
  let seriesTakes = [];
  $: isSeries = isListenRepeatSeries(
    exerciseDetail?.exerciseType,
    exerciseDetailData,
  );
  $: seriesSentenceCount = isSeries
    ? listenRepeatSentences(exerciseDetailData).length
    : 0;

  // The recorder hands over its file shortly after it stops.
  async function waitForRecording(timeoutMs = 2000) {
    const started = Date.now();
    while (!$lastRecording?.blob && Date.now() - started < timeoutMs) {
      await sleep(100);
    }
    return $lastRecording?.blob;
  }

  async function finishSeriesSentence(segments, noAudioReason) {
    if (isSubmitting) {
      console.warn("finishSeriesSentence: Already submitting, ignoring duplicate call");
      return;
    }
    isSubmitting = true;
    isProcessing = true;

    // A retried upload of the last sentence must not record it again.
    if (seriesTakes[sentenceIndex] === undefined) {
      const blob = await waitForRecording();
      const fileName = seriesRecordingFileName(
        uploadMediaFileName(instanceId, exerciseId),
        sentenceIndex,
      );
      seriesTakes[sentenceIndex] = {
        aggregate: { ...aggregateScore },
        segments: [...segments],
        noAudioReason: segments.length === 0 ? noAudioReason : undefined,
        blob,
        recordingFileName: fileName,
        recordingFileUrl: uploadMediaFileUrl(instanceId, exerciseId, fileName),
      };
    }

    if (sentenceIndex < seriesSentenceCount - 1) {
      startNextSentence();
    } else {
      submitSeriesResult();
    }
  }

  function startNextSentence() {
    const tokenFailed = noAudioReason === "TOKEN_FAILURE" || noAudioReason === "AUTH_FAILURE";
    sentenceIndex += 1;
    isSubmitting = false;
    isProcessing = false;
    submitErrorMessage = "";
    canRecordNoAudio = false;
    noAudioReason = "";
    showEarlyStopWarning = false;
    $recognitionResultsFinal = [];
    $lastRecording = undefined;
    aggregateScore = {};
    audioStatus = "NOT_STARTED";
    promptAudioDone = false;
    promptStep = 0;
    $sharedTimer = exerciseDetail.prepareLimit ? exerciseDetail.prepareLimit : 60;
    $sharedTimerMode = "PREPARE";
    if (!window.SpeechSDK) {
      // No speech SDK: only the skip stays available, as on the first sentence.
      ttsButtonState = "error";
      canRecordNoAudio = true;
      noAudioReason = "SDK_MISSING";
    } else if (ttsToken && !tokenFailed) {
      ttsButtonState = "ready";
    } else {
      // The previous sentence was skipped because the token never came:
      // fetch it again before Record is offered (failure re-enables Skip).
      refreshToken();
    }
  }

  function submitSeriesResult() {
    const { data, rawData } = combineSeriesTakes(seriesTakes);
    const spoken = data.sentences.some((s) => s.status === "OK");
    aggregateScore = {
      ...data,
      mode: "SERIES",
      timerRemaining: $sharedTimer,
      audioFinished: audioStatus === "ENDED",
    };
    if (!spoken) {
      // Same marker as a single recording without speech: scored 0.
      aggregateScore.status = "NO_AUDIO";
      aggregateScore.noAudioReason = seriesTakes[0]?.noAudioReason || "EMPTY_RECOGNITION";
    }

    const uploads = seriesTakes
      .filter((take) => take?.blob)
      .map((take) =>
        uploadMedia(instanceId, exerciseId, take.blob, "azure", take.recordingFileName),
      );
    uploads.push(
      uploadExerciseResults(
        instanceId,
        exerciseId,
        aggregateScore,
        rawData,
        seriesTakes[0]?.recordingFileName,
      ),
    );

    const UPLOAD_TIMEOUT = 60000 + 15000 * seriesTakes.length;
    Promise.race([
      Promise.all(uploads),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Upload timeout - please check your connection')), UPLOAD_TIMEOUT)
      ),
    ])
      .then(() => {
        console.log("Successfully sent series data");
        isSubmitting = false;
        submitErrorMessage = "";
        goToNextExercise();
      })
      .catch((e) => {
        lastErrorPhase = "upload";
        trackError("submitSeriesResult upload failed", e, {
          submitPath: "listen-repeat-series",
          sentenceCount: seriesTakes.length,
        });
        isSubmitting = false;
        isProcessing = false;
        ttsButtonState = "error";
        // The kept sentences stay; REFRESH (submitRetry) uploads them again.
        submitErrorMessage = $_("UPLOAD_FAILED_RETRY");
      });
  }

  function audioStarted() {
    console.log("audio started");
    audioStatus = "STARTED";
    introTimerComponent?.resumeTimer();
  }

  function audioNotStarted() {
    console.log("audio not started");
    audioStatus = "NOT_STARTED";
    introTimerComponent?.pauseTimer();
  }

 function audioEnded() {
    console.log("audio ended");
    audioStatus = "ENDED";
  }

  function imageInit() {
    console.log("imageInit");
    imageStatus = "INIT";
    // introTimerComponent?.pauseTimer();
  }

  function imageLoaded() {
    console.log("imageLoaded");
    imageStatus = "LOADED";
    // introTimerComponent?.resumeTimer();
  }

  function imageError() {
    console.log("imageError");
    imageStatus = "ERROR";
    // introTimerComponent?.resumeTimer();
  }

  function hasDisplayText(value) {
    if (!value) return false;
    const plain = String(value)
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim();
    return plain.length > 0;
  }
</script>

<div class:mc-combined-outer={isMCCombined}>
  <div
    class="container exercise-container"
    class:mc-combined-container={isMCCombined}
    class:writing-container={exerciseDetail?.exerciseType === "WRITING"}
  >
    <div bind:this={sentinelDiv} class="sentinel"></div>
    {#if exerciseStatus === "ERROR"}
      <div class="exercise-loading-shell" role="alert" aria-live="assertive">
        <div class="overview-status-card">
          <p class="submit-error">Unable to load this question. Please try again.</p>
          <button class="btn btn-primary btn-cta" on:click={() => loadData(exerciseId)}>{$_("RETRY")}</button>
        </div>
      </div>
    {:else if exerciseStatus !== "OK" || !exerciseDetail}
      {#if hasLoadedExerciseOnce}
      <div class="exercise-loading-shell" aria-live="polite" aria-busy="true">
        <div class="exercise-loading-columns">
          <div class="loading-shimmer loading-column-pane"></div>
          <div class="loading-shimmer loading-column-pane loading-column-pane-editor"></div>
        </div>

        <div class="loading-shimmer loading-submit"></div>
        <span class="sr-only">Loading assessment content...</span>
      </div>
      {:else}
        <div class="exercise-loading-pass-through" aria-hidden="true"></div>
      {/if}
    {:else}
      <div id="pageHeader">
        <div
          class="row top-status-bar align-items-center"
        >
          <div class="col" style="display:none;">
            <div class="exercise-title">
              {#if exerciseDetail}
                <span class="exercise-name" />{exerciseDetail?.name}
                <span style="display: none;"
                  >({exerciseDetail?.exerciseType})</span
                >
              {/if}
            </div>
          </div>

          <!-- <div class="col">
            <ProgressDetails {instanceId}></ProgressDetails>
          </div> -->
          <div class="col-12 progress-details-center">
            <ProgressDetails3 {instanceId}></ProgressDetails3>
          </div>

          <div class="col-12 d-flex justify-content-center">
            <div class="top-status-timer">
              {#if exerciseDetail.exerciseType === "INTRO" || exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" || exerciseDetail.exerciseType === "MULTICHOICE_SELECT" || exerciseDetail.exerciseType === "MULTICHOICE_COMBINED" || exerciseDetail.exerciseType === "FILL_THE_GAP" || exerciseDetail.exerciseType === "FILL_THE_LETTERS" || exerciseDetail.exerciseType === "DRAG_WORDS" || exerciseDetail.exerciseType === "WRITING"}
                {#key `${exerciseId}-${exerciseDetail.exerciseType}`}
                  <IntroTimer
                    bind:this={introTimerComponent}
                    time={exerciseDetail.durationLimit}
                    on:counterStop={stopFromTimerOnly}
                  />
                {/key}
              {:else if exerciseDetail.exerciseType !== "PHOTO_VERIFICATION"}
                <SharedTimer />
              {/if}
            </div>
          </div>

          <div class="col lesson-detail-box" style="display:none;">
            <div class="lesson-detail float-right">
              <div>Assessment: {$assessmentId}</div>
              <div style="display: none;">Instance: {instanceId}</div>
              <div>{$_("USER_NAME")}: {$userName}</div>
              <div style="display: none;">UserId: {$userId}</div>
              <div style="display: none;">State: {$assessmentState}</div>
            </div>
          </div>
        </div>
        <!-- <Instructions exerciseType={exerciseDetail.exerciseType} /> -->

      </div>

      <div class="row" class:mc-combined-jumbotron-row={isMCCombined}>
        <div class="col" class:mc-combined-col={isMCCombined}>
          <div
            class="jumbotron"
            class:mc-combined-jumbotron={isMCCombined}
            on:contextmenu|preventDefault={() => {
              console.log("contextmenu");
            }}
          >
            {#if exerciseDetail.exerciseType === "INTRO"}
              <div class="intro-directions">
                {#if sectionNumber >= 1}
                  <h1 class="intro-title">Section {sectionNumber}</h1>
                {/if}
                <p class="intro-desc">{@html fixPunctuation(exerciseDetailData.text) || ""}</p>
                {#if (introQuestionCount !== null || introDurationLimit !== null) && !assessmentTypeKey.includes("IELT")}
                  <div class="intro-stats">
                    {#if introQuestionCount !== null}
                      <div class="stat-card">
                        <p class="stat-label">NUMBER OF QUESTIONS</p>
                        <p class="stat-value">{introQuestionCount}</p>
                      </div>
                    {/if}
                    {#if introDurationLimit !== null}
                      <div class="stat-card">
                        <p class="stat-label">TIME PER QUESTION</p>
                        <p class="stat-value">{Math.floor(introDurationLimit / 60)}:{String(introDurationLimit % 60).padStart(2, "0")}</p>
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "PHOTO_VERIFICATION"}
              <p class="textToRead no-select photo-verify-text" style="text-align:center;">
                {@html fixPunctuation(exerciseDetailData.text) || ""}
              </p>
              <PhotoVerification
                bind:this={photoVerification}
                {instanceId}
                {exerciseId}
                {exerciseDetailData}
                on:initPhotoVerification={onPhotoVerificationInit}
                on:photoUploaded={onPhotoUploaded}
                on:continueToNext={(e) => onPhotoVerificationComplete(e.detail)}
                storageProvider="azure"
              ></PhotoVerification>
            {:else if exerciseDetail.exerciseType === "LISTEN_REPEAT"}
              <!-- The sentence is heard, never shown: `text` is only what the
                   pronunciation assessment listens for. -->
              <div class="speaking-card">
                {#if hasDisplayText(exerciseDetailData?.scenario)}
                  <div class="textToRead no-select listen-repeat-scenario">
                    {@html fixPunctuation(exerciseDetailData.scenario) || ""}
                  </div>
                {/if}
                {#if isSeries}
                  <p class="listen-repeat-progress">
                    {$_("SENTENCE_OF", { values: { current: sentenceIndex + 1, total: seriesSentenceCount } })}
                  </p>
                {/if}
                <p class="textToRead no-select listen-repeat-prompt">
                  {$_("LISTEN_AND_REPEAT_PROMPT")}
                </p>
                {#if hasPromptAudio && $sharedTimerMode === "PREPARE"}
                  {#key sentenceIndex}
                    <PromptAudio
                      queue={promptQueue}
                      step={promptStep}
                      maxPlays={promptPlayback.maxPlays}
                      autoplay={promptPlayback.autoplay}
                      labels={{ sentence: $_("LISTEN_TO_THE_SENTENCE") }}
                      on:audioStarted={audioStarted}
                      on:promptEnded={promptAudioEnded}
                      on:promptFailed={promptAudioFailed}
                    />
                  {/key}
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "READ"}
              <div class="speaking-card">
                {#if hasPromptAudio}
                  {#if listenRepeatTextHidden}
                    <p class="textToRead no-select listen-repeat-prompt">
                      {$_("LISTEN_AND_REPEAT_PROMPT")}
                    </p>
                  {/if}
                  <!-- Only while preparing: once recording starts the player
                       unmounts, so the microphone never picks up playback. -->
                  {#if $sharedTimerMode === "PREPARE"}
                    <PromptAudio
                      queue={promptQueue}
                      step={promptStep}
                      maxPlays={promptPlayback.maxPlays}
                      autoplay={promptPlayback.autoplay}
                      labels={{ prompt: $_("LISTEN_TO_THE_SENTENCE") }}
                      on:audioStarted={audioStarted}
                      on:promptEnded={promptAudioEnded}
                      on:promptFailed={promptAudioFailed}
                    />
                  {/if}
                {/if}
                {#if !listenRepeatTextHidden}
                  <p class="textToRead no-select">
                    {@html fixPunctuation(exerciseDetailData.text) || ""}
                  </p>
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "QUESTION"}
              <div class="speaking-card">
              {#if exerciseDetailData?.imageFileName}
                <div class="listen-media-center">
                  <div class="mt-2 mb-2">
                    {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                      <ImageLoader src={exerciseDetailData?.imageFileName}
                       on:imageInit={imageInit}
                       on:imageLoaded={imageLoaded}
                       on:imageError={imageError}
                      ></ImageLoader>
                    {:else}
                      <ImageLoader
                        src={"https://storage.googleapis.com/sensay-assessment/images/" +
                          exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                    {/if}
                  </div>
                </div>
              {/if}
                <!-- An empty question is heard only (TOEIC Q8-10). -->
                {#if hasDisplayText(exerciseDetailData?.question)}
                  <p class="textToRead no-select">
                    {@html fixPunctuation(exerciseDetailData.question) || ""}
                  </p>
                {/if}
                <!-- Same rule as READ: the players only exist while preparing,
                     so the microphone never picks up playback. The
                     introduction, if any, plays before the question; its
                     transcript is never shown. -->
                {#if hasPromptAudio && $sharedTimerMode === "PREPARE"}
                  <PromptAudio
                    queue={promptQueue}
                    step={promptStep}
                    maxPlays={promptPlayback.maxPlays}
                    autoplay={promptPlayback.autoplay}
                    labels={{ intro: $_("LISTEN_TO_THE_INTRODUCTION") }}
                    on:audioStarted={audioStarted}
                    on:promptEnded={promptAudioEnded}
                    on:promptFailed={promptAudioFailed}
                  />
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "MULTICHOICE"}
              <p class="textToRead no-select">
                {@html fixPunctuation(exerciseDetailData.question) || ""}
              </p>

              <ol class="list-group list-group-numbered no-select">
                {#each exerciseDetailData?.options as o, i}
                  <li class="list-group-item">
                    {#if o?.key}{o.key}{:else}{i + 1}{/if}: {o?.text}
                  </li>
                {/each}
              </ol>
            {:else if exerciseDetail.exerciseType === "LISTEN"}
              <div class="speaking-card">
                <p class="textToRead no-select">
                  {@html fixPunctuation(exerciseDetailData.question) || ""}
                </p>
                <div class="float-left">
                  <TextToSpeech2
                    bind:this={ttsInstance}
                    textToRead={exerciseDetailData.text}
                    language={exerciseDetail.language}
                  />
                </div>
              </div>
            {:else if exerciseDetail.exerciseType === "IMAGE"}
              <div class="speaking-card">
              {#if exerciseDetailData?.imageFileName}
                <div class="listen-media-center">
                  <div class="mt-2 mb-2">
                    {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                      <ImageLoader src={exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                    {:else}
                      <ImageLoader
                        src={"https://storage.googleapis.com/sensay-assessment/images/" +
                          exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                    {/if}
                  </div>
                </div>
              {/if}
              <p class="textToRead no-select">
                {@html fixPunctuation(exerciseDetailData.question) || ""}
              </p>
              </div>
            {:else if exerciseDetail.exerciseType === "IMAGE"}
              <div class="speaking-card">
              {#if exerciseDetailData?.imageFileName}
                <div class="listen-media-center">
                  <div class="mt-2 mb-2">
                    {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                      <ImageLoader src={exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                    {:else}
                      <ImageLoader
                        src={"https://storage.googleapis.com/sensay-assessment/images/" +
                          exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                    {/if}
                  </div>
                </div>
              {/if}
                <p class="textToRead no-select">
                  {@html fixPunctuation(exerciseDetailData.question) || ""}
                </p>
              </div>
            {:else if exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE"}
              <div class="lwi-layout">
                <!-- Left: image + audio -->
                <div class="lwi-media-col">
                  {#if hasDisplayText(exerciseDetailData?.question)}
                    <p class="textToRead no-select">
                      {@html fixPunctuation(exerciseDetailData.question) || ""}
                    </p>
                  {:else}
                    <div class="empty-question-spacer" aria-hidden="true"></div>
                  {/if}

                  {#if exerciseDetailData?.imageFileName}
                    <div class="listen-media-center">
                      <div class="mt-2 mb-2">
                        {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                          <ImageLoader src={exerciseDetailData?.imageFileName}></ImageLoader>
                        {:else}
                          <ImageLoader
                            src={"https://storage.googleapis.com/sensay-assessment/images/" +
                              exerciseDetailData?.imageFileName}
                          ></ImageLoader>
                        {/if}
                      </div>
                    </div>
                  {/if}

                  {#if exerciseDetailData?.audioFileName}
                    <div class="listen-media-center mt-2 mb-2">
                      {#key exerciseDetailData?.audioFileName}
                        <AudioPlayback
                          src={exerciseDetailData?.audioFileName}
                          on:audioLoading={audioNotStarted}
                          on:audioStarted={audioStarted}
                          on:audioError={audioNotStarted}
                          on:audioReady={audioNotStarted}
                          on:audioEnded={audioEnded}
                        />
                      {/key}
                    </div>
                  {/if}
                </div>

                <!-- Right: answer options -->
                {#if exerciseDetailData?.optionA}
                  <div class="lwi-options-col">
                    <ol class="list-group list-group-numbered">
                      {#each ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"] as letter}
                        {#if exerciseDetailData?.["option" + letter]}
                          <li
                            class="list-group-item score"
                            class:active={selectedOption === letter}
                            on:click={() => { selectOption(letter); }}
                          >{@html fixPunctuation(exerciseDetailData?.["option" + letter]) || ""}</li>
                        {/if}
                      {/each}
                    </ol>
                  </div>
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "MULTICHOICE_SELECT"}
              {#if exerciseDetailData.question}
                <p class="textToRead mc-select-question">
                  {@html fixPunctuation(exerciseDetailData.question) || ""}
                </p>
              {/if}

              {#if exerciseDetailData?.imageFileName}
                <div align="center">
                  <div class="mt-2 mb-2">
                    {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                      <ImageLoader src={exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                      <!-- <img
                        class="customImage"
                        src={exerciseDetailData?.imageFileName}
                        alt="Question"
                      /> -->
                    {:else}
                      <ImageLoader
                        src={"https://storage.googleapis.com/sensay-assessment/images/" +
                          exerciseDetailData?.imageFileName}
                      ></ImageLoader>
                      <!-- <img
                        class="customImage"
                        src={"https://storage.googleapis.com/sensay-assessment/images/" +
                          exerciseDetailData?.imageFileName}
                        alt="Question"
                      /> -->
                    {/if}
                  </div>
                </div>
              {/if}

              {#if exerciseDetailData?.optionA}
                <div>
                  <ol class="list-group list-group-numbered">
                    {#each ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"] as letter}
                      {#if exerciseDetailData?.["option" + letter]}
                        <li
                          class="list-group-item score"
                          class:active={selectedOption === letter}
                          on:click={() => {
                            selectOption(letter);
                          }}
                        >
                          {@html fixPunctuation(exerciseDetailData?.["option" + letter]) || ""}
                        </li>
                      {/if}
                    {/each}
                  </ol>
                </div>
              {/if}
            {:else if exerciseDetail.exerciseType === "MULTICHOICE_COMBINED"}
              <div class="row g-4 mc-combined-row">
                <!-- Left Column: Passage -->
                <div class="col-lg-6 passage-section">
                  {#if exerciseDetailData?.imageFileName || exerciseDetailData?.audioFileName}
                    <div class="question-media-card no-select mb-3">
                      {#if exerciseDetailData?.imageFileName}
                        <div class="listen-media-center">
                          <div class="mt-2 mb-2">
                            {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                              <ImageLoader src={exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {:else}
                              <ImageLoader
                                src={"https://storage.googleapis.com/sensay-assessment/images/" +
                                  exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {/if}
                          </div>
                        </div>
                      {/if}

                      {#if exerciseDetailData?.audioFileName}
                        <div class="listen-media-center mt-2 mb-2">
                          {#key exerciseDetailData?.audioFileName}
                            <AudioPlayback
                              src={exerciseDetailData?.audioFileName}
                              on:audioLoading={audioNotStarted}
                              on:audioStarted={audioStarted}
                              on:audioError={audioNotStarted}
                              on:audioReady={audioNotStarted}
                              on:audioEnded={audioEnded}
                            />
                          {/key}
                        </div>
                      {/if}
                    </div>
                  {/if}

                  {#if hasMcCombinedPassageContent}
                    {#if passageTabs?.length > 1}
                      <div class="passage-tabs-nav mb-2">
                        <button
                          type="button"
                          class="passage-arrow"
                          disabled={activePassageIndex === 0}
                          on:click={() => (activePassageIndex = activePassageIndex - 1)}
                          aria-label="Previous passage"
                        >&#8249;</button>
                        <div class="passage-tabs">
                          {#each passageTabs as passage, index}
                            <button
                              type="button"
                              class="passage-tab {index === activePassageIndex ? 'active' : ''}"
                              on:click={() => (activePassageIndex = index)}
                            >
                              {passage.title}
                            </button>
                          {/each}
                        </div>
                        <button
                          type="button"
                          class="passage-arrow"
                          disabled={activePassageIndex === passageTabs.length - 1}
                          on:click={() => (activePassageIndex = activePassageIndex + 1)}
                          aria-label="Next passage"
                        >&#8250;</button>
                      </div>
                    {/if}
                    <div class="passage-content"
                      class:swipeable={passageTabs?.length > 1}
                      on:touchstart={onPassageTouchStart}
                      on:touchend={onPassageTouchEnd}
                    >
                      {#if passageTabs?.length > 1}
                        <button
                          type="button"
                          class="passage-float-arrow left"
                          disabled={activePassageIndex === 0}
                          on:click|stopPropagation={() => (activePassageIndex -= 1)}
                          aria-label="Previous passage"
                        >&#8249;</button>
                        <button
                          type="button"
                          class="passage-float-arrow right"
                          disabled={activePassageIndex === passageTabs.length - 1}
                          on:click|stopPropagation={() => (activePassageIndex += 1)}
                          aria-label="Next passage"
                        >&#8250;</button>
                      {/if}
                      {#if passageTabs?.[activePassageIndex]}
                        {@const dialogueData = parseDialogueContent(passageTabs[activePassageIndex].content)}
                        {@const emailData = !dialogueData ? parseEmailContent(passageTabs[activePassageIndex].content) : null}
                        {#if dialogueData}
                          <div class="dialogue-wrapper">
                            {#each dialogueData.messages as msg}
                              {@const isLeft = msg.speaker === dialogueData.firstSpeaker}
                              <div class="dialogue-row {isLeft ? 'left' : 'right'}">
                                <div class="dialogue-meta">[{msg.time}] {msg.speaker}:</div>
                                <div class="dialogue-bubble {isLeft ? 'bubble-left' : 'bubble-right'}">{msg.text}</div>
                              </div>
                            {/each}
                          </div>
                        {:else if emailData}
                          <div class="email-wrapper">
                              <div class="email-header-block">
                                {#each emailData.headers as h}
                                  <div class="email-header-row">
                                    <span class="email-label">{h.label}:</span>
                                    <span class="email-value">{h.value}</span>
                                  </div>
                                {/each}
                              </div>
                              <div class="email-body">{@html fixPunctuation(emailData.bodyHtml)}</div>
                            </div>
                        {:else}
                          <div class="textToRead">
                            {@html fixPunctuation(passageTabs[activePassageIndex].content) || ''}
                          </div>
                        {/if}
                      {:else}
                        <p class="textToRead">{@html fixPunctuation(exerciseDetailData.text) || ""}</p>
                      {/if}
                    </div>
                  {/if}
                </div>

                <!-- Right Column: Questions -->
                <div class="col-lg-6 questions-section">
                  {#if exerciseDetailData?.questions?.length > 0}
                    {#each exerciseDetailData?.questions as q, i}
                      <div class="question-item {i > 0 ? 'mt-4' : ''}">
                        <div class="question-text">
                          <div class="textToRead">{@html fixPunctuation(q.question)}</div>
                        </div>
                        <div class="question-options">
                          <ol class="list-group list-group-numbered">
                            {#each ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"] as letter}
                              {#if q?.["option" + letter]}
                                <li
                                  class="list-group-item score"
                                  class:active={selectedOptionList[i] === letter}
                                  on:click={() => {
                                    selectOptions(i, letter);
                                  }}
                                >
                                  {@html fixPunctuation(q?.["option" + letter]) || ""}
                                </li>
                              {/if}
                            {/each}
                          </ol>
                        </div>
                      </div>
                    {/each}
                  {/if}
                </div>
              </div>
            {:else if exerciseDetail.exerciseType === "FILL_THE_GAP"}
              <!-- Inline answer fields are injected into the question/text HTML
                   (replaceGapTokens); their input/change events bubble up to
                   these delegated handlers. -->
              <div
                class="row g-4 gap-row"
                on:input={handleGapFieldEvent}
                on:change={handleGapFieldEvent}
              >
                <div class="{gapPromptColClass} gap-prompt-col">
                  {#if exerciseDetailData?.imageFileName || exerciseDetailData?.audioFileName}
                    <div class="gap-question-card gap-content no-select">
                      {#if exerciseDetailData?.imageFileName}
                        <div class="listen-media-center">
                          <div class="mt-2 mb-2">
                            {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                              <ImageLoader src={exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {:else}
                              <ImageLoader
                                src={"https://storage.googleapis.com/sensay-assessment/images/" +
                                  exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {/if}
                          </div>
                        </div>
                      {/if}

                      {#if exerciseDetailData?.audioFileName}
                        <div class="listen-media-center mt-2 mb-2">
                          {#key exerciseDetailData?.audioFileName}
                            <AudioPlayback
                              src={exerciseDetailData?.audioFileName}
                              on:audioLoading={audioNotStarted}
                              on:audioStarted={audioStarted}
                              on:audioError={audioNotStarted}
                              on:audioReady={audioNotStarted}
                              on:audioEnded={audioEnded}
                            />
                          {/key}
                        </div>
                      {/if}
                    </div>
                  {/if}

                  {#if hasDisplayText(exerciseDetailData?.text)}
                    <div class="gap-exercise gap-content">
                      <div class="textToRead gap-passage">
                        {@html gapTextHtml}
                      </div>
                    </div>
                  {/if}
                </div>

                {#if gapHasAnswerCol}
                  <div class="{gapAnswerColClass} gap-answer-col">
                    {#if gapHasQuestion}
                      <div class="gap-question-card gap-content no-select">
                        <div class="textToRead gap-passage">
                          {@html gapQuestionHtml}
                        </div>
                      </div>
                    {/if}

                    <!-- Only gaps whose __N__ placeholder was found in neither
                         field land here; the rest are typed inline above. -->
                    {#if gapHasFallback}
                      <div class="gap-answer-card">
                        <div class="gap-fallback-list">
                          {#each gapFallbackIndexes as gi}
                            {@const gap = (exerciseDetailData?.gaps || [])[gi]}
                            <div class="gap-fallback-item">
                              <label
                                class="gap-fallback-label"
                                for={"gap-answer-" + gi}
                              >
                                {gap?.label || `Gap ${gi + 1}`}
                              </label>
                              {#if gap?.fieldType === "DROPDOWN"}
                                <select
                                  id={"gap-answer-" + gi}
                                  class="gap-field gap-select"
                                  value={gapAnswers[gi] ?? ""}
                                  on:change={(e) =>
                                    setGapAnswer(gi, e.target.value)}
                                >
                                  <option value=""></option>
                                  {#each gap?.options || [] as o}
                                    <option value={o}>{o}</option>
                                  {/each}
                                </select>
                              {:else}
                                <input
                                  id={"gap-answer-" + gi}
                                  type="text"
                                  class="gap-field gap-input"
                                  autocomplete="off"
                                  autocapitalize="off"
                                  spellcheck="false"
                                  value={gapAnswers[gi] ?? ""}
                                  on:input={(e) =>
                                    setGapAnswer(gi, e.target.value)}
                                />
                              {/if}
                            </div>
                          {/each}
                        </div>
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            {:else if isTextBlankType}
              <div class="text-blank-exercise">
                {#if exerciseDetailData?.imageFileName || exerciseDetailData?.audioFileName}
                  <div class="question-media-card no-select mb-2">
                    {#if exerciseDetailData?.imageFileName}
                      <div class="listen-media-center">
                        <div class="mt-2 mb-2">
                          {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                            <ImageLoader src={exerciseDetailData?.imageFileName}
                            ></ImageLoader>
                          {:else}
                            <ImageLoader
                              src={"https://storage.googleapis.com/sensay-assessment/images/" +
                                exerciseDetailData?.imageFileName}
                            ></ImageLoader>
                          {/if}
                        </div>
                      </div>
                    {/if}

                    {#if exerciseDetailData?.audioFileName}
                      <div class="listen-media-center mt-2 mb-2">
                        {#key exerciseDetailData?.audioFileName}
                          <AudioPlayback
                            src={exerciseDetailData?.audioFileName}
                            on:audioLoading={audioNotStarted}
                            on:audioStarted={audioStarted}
                            on:audioError={audioNotStarted}
                            on:audioReady={audioNotStarted}
                            on:audioEnded={audioEnded}
                          />
                        {/key}
                      </div>
                    {/if}
                  </div>
                {/if}

                {#if exerciseDetail.exerciseType === "FILL_THE_LETTERS" && letterExercise}
                  <FillTheLetters
                    exercise={letterExercise}
                    bind:answers={letterAnswers}
                    showMissing={showAnswerWarning}
                    disabled={isProcessing}
                  />
                {:else if exerciseDetail.exerciseType === "DRAG_WORDS" && dragExercise}
                  <DragWords
                    exercise={dragExercise}
                    bind:placements={dragPlacements}
                    showMissing={showAnswerWarning}
                    disabled={isProcessing}
                  />
                {/if}
              </div>
            {:else if exerciseDetail.exerciseType === "WRITING"}
              <div class="row g-4 writing-row">
                <!-- Left: prompt -->
                <div class="col-lg-6 writing-prompt-col">
                  <div class="writing-prompt textToRead no-select">
                    {@html fixPunctuation(exerciseDetailData.question) || ""}
                  </div>

                  {#if exerciseDetailData?.imageFileName || exerciseDetailData?.audioFileName}
                    <div class="question-media-card no-select mb-3">
                      {#if exerciseDetailData?.imageFileName}
                        <div class="listen-media-center">
                          <div class="mt-2 mb-2">
                            {#if exerciseDetailData?.imageFileName?.startsWith("https")}
                              <ImageLoader src={exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {:else}
                              <ImageLoader
                                src={"https://storage.googleapis.com/sensay-assessment/images/" +
                                  exerciseDetailData?.imageFileName}
                              ></ImageLoader>
                            {/if}
                          </div>
                        </div>
                      {/if}

                      {#if exerciseDetailData?.audioFileName}
                        <div class="listen-media-center mt-2 mb-2">
                          {#key exerciseDetailData?.audioFileName}
                            <AudioPlayback
                              src={exerciseDetailData?.audioFileName}
                              on:audioLoading={audioNotStarted}
                              on:audioStarted={audioStarted}
                              on:audioError={audioNotStarted}
                              on:audioReady={audioNotStarted}
                              on:audioEnded={audioEnded}
                            />
                          {/key}
                        </div>
                      {/if}
                    </div>
                  {/if}
                </div>

                <!-- Right: editor -->
                <div class="col-lg-6 writing-editor-col">
                  <div class="writing-editor-card">
                    <textarea
                      class="writing-textarea"
                      class:copy-paste-allowed={writingAllowCopyPaste}
                      value={writingText}
                      placeholder={$_("WRITING_PLACEHOLDER")}
                      spellcheck="false"
                      autocomplete="off"
                      autocapitalize="off"
                      disabled={isProcessing}
                      on:input={handleWritingInput}
                      on:focus={() => logWritingEvent("FOCUS_GAINED")}
                      on:blur={() => logWritingEvent("FOCUS_LOST")}
                      on:paste={(e) => handleWritingGuard(e, "PASTE_ATTEMPT", "PASTE_DISABLED_WARNING")}
                      on:copy={(e) => handleWritingGuard(e, "COPY_ATTEMPT", "COPY_DISABLED_WARNING")}
                      on:cut={(e) => handleWritingGuard(e, "CUT_ATTEMPT", "COPY_DISABLED_WARNING")}
                      on:drop={(e) => handleWritingGuard(e, "DROP_ATTEMPT", "PASTE_DISABLED_WARNING")}
                      on:contextmenu={(e) => {
                        if (!writingAllowCopyPaste) e.preventDefault();
                      }}
                    ></textarea>
                    <div class="writing-editor-footer">
                      <span
                        class="writing-word-count"
                        class:under-minimum={writingMinWords > 0 &&
                          writingWordCount < writingMinWords}
                      >
                        {$_("WORD_COUNT")}: {writingWordCount}
                      </span>
                    </div>
                    {#if writingWarning}
                      <p class="writing-warning writing-warning-below">{writingWarning}</p>
                    {/if}
                  </div>
                </div>
              </div>
            {:else if exerciseDetail.exerciseType === "VIDEO"}
              {#if exerciseDetailData.youtubeUrl}
                <iframe
                  title="youtube video"
                  width="500"
                  height="350"
                  src={exerciseDetailData.youtubeUrl}
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                />
              {:else if exerciseDetailData.apiVideoUrl}
                <iframe
                  title="ApiVideo"
                  src={exerciseDetailData.apiVideoUrl}
                  width="100%"
                  height="350"
                  frameborder="0"
                  scrolling="no"
                  allowfullscreen="true"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              {/if}
              <h3 class="textToRead">{exerciseDetailData.question}</h3>
            {:else}Wrong Type
            {/if}
            {#if textToRead}
              <!-- <TextToSpeech textToRead={textToRead} language={exerciseDetail.language}></TextToSpeech> -->
              <div class="float-right">
                <TextToSpeech2
                  {textToRead}
                  language={exerciseDetail.courseLanguage}
                />
              </div>
            {/if}
          </div>
        </div>
      </div>

      <div class:bigger-bottom={isCollapsed} class:mc-combined-footer={isMCCombined}>
        {#if exerciseDetail.exerciseType === "INTRO"}
          <div class="row justify-content-center mt-2 mb-4">
            <div class="col col-auto" style="display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
              {#if submitErrorMessage}
                <p class="submit-error">{submitErrorMessage}</p>
                <button class="btn btn-primary btn-cta" on:click={retrySubmitSkip}>{$_("RETRY")}</button>
              {/if}
              <button
                class="btn-intro-start"
                disabled={isProcessing}
                on:click={stopFromTimerOnly}
                >{#if isProcessing}<span class="state-blink">Submitting...</span>{:else}START{/if}</button
              >
            </div>
          </div>
        {:else if exerciseDetail.exerciseType === "LISTEN_WITH_IMAGE" || exerciseDetail.exerciseType === "MULTICHOICE_SELECT" || exerciseDetail.exerciseType === "MULTICHOICE_COMBINED" || exerciseDetail.exerciseType === "FILL_THE_GAP" || exerciseDetail.exerciseType === "FILL_THE_LETTERS" || exerciseDetail.exerciseType === "DRAG_WORDS" || exerciseDetail.exerciseType === "WRITING"}
          <div class="row justify-content-center">
            <div style="display:none;" class="col">
              {ttsButtonState} - {ttsToken}
            </div>
            <div class="col col-auto" style="display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
              {#if showAnswerWarning}
                <p class="answer-warning">{$_("PLEASE_ANSWER_ALL")}</p>
              {/if}
              {#if submitErrorMessage}
                <p class="submit-error">{submitErrorMessage}</p>
                <button class="btn btn-primary btn-cta" on:click={retrySubmitSkip}>{$_("RETRY")}</button>
              {/if}
              <button
                class:btn-disabled={!isNextAllowed && !isProcessing}
                class="btn-intro-start"
                disabled={isProcessing}
                on:click={onContinueClick}
                >{#if isProcessing}<span class="state-blink">{$_("SUBMITTING")}</span>{:else}{$_("CONTINUE")}{/if}</button
              >
            </div>
          </div>
        {:else if exerciseDetail.exerciseType === "PHOTO_VERIFICATION"}
          <div class="row justify-content-center">
            <div style="display:none;" class="col">
              {ttsButtonState} - {ttsToken}
            </div>
            <div class="col col-auto" style="display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
              {#if submitErrorMessage}
                <p class="submit-error">{submitErrorMessage}</p>
                <button class="btn btn-primary btn-cta" on:click={retrySubmitSkip}>{$_("RETRY")}</button>
              {/if}
              <button
                class="btn-intro-start"
                disabled={!isNextAllowed || isProcessing}
                class:btn-disabled={!isNextAllowed && !isProcessing}
                on:click={stopFromTimerOnly}
                >{#if isProcessing}<span class="state-blink">Submitting...</span>{:else}Submit{/if}</button
              >
            </div>
          </div>
        {:else}
          <div class="row justify-content-center">
            <div style="display:none;" class="col">
              {ttsButtonState} - {ttsToken}
            </div>
            <div class="col col-auto">
              {#if ttsButtonState === "starting"}...
              {:else if ttsButtonState === "error"}
                <div style="display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
                  {#if submitErrorMessage}
                    <p class="submit-error">{submitErrorMessage}</p>
                  {/if}
                  <button
                    class="btn btn-primary btn-lg btn-cta"
                    on:click={submitRetry}><MicSvg />{$_("REFRESH")}</button
                  >
                  {#if canRecordNoAudio}
                    <!-- Speech could not be captured. Let the student move on with
                         an explicit NO_AUDIO result so the exercise is never lost. -->
                    <button
                      class="btn btn-outline-secondary btn-cta"
                      disabled={isProcessing}
                      on:click={() => submitNoAudioResult(noAudioReason || "SPEECH_ERROR")}
                      >{$_("SKIP")}</button
                    >
                  {/if}
                </div>
              {:else if isProcessing || ttsButtonState === "stop" || ttsButtonState === "done" || ttsButtonState === "timeout"}
                <button class="btn-intro-start" disabled>
                  <span class="state-blink">Processing...</span>
                </button>
              {:else if ttsButtonState === "ready"}
                <!-- Answering starts only once the prompt recording has been
                     heard: starting unmounts the player, and with a hidden
                     Listen & Repeat sentence that recording is the only prompt.
                     The hold also lifts if the recording fails to load. -->
                <div style="display:flex;flex-direction:column;align-items:center;">
                  {#if holdPrepareTimer}
                    <p class="prompt-audio-first">{$_("PROMPT_AUDIO_LISTEN_FIRST")}</p>
                  {/if}
                  <button
                    class="btn btn-primary btn-lg btn-cta"
                    disabled={holdPrepareTimer}
                    on:click={startRecognition}><MicSvg />{exerciseDetail?.exerciseType === "LISTEN_REPEAT" ? $_("RECORD") : ["READ","QUESTION","LISTEN","IMAGE"].includes(exerciseDetail?.exerciseType) && $sharedTimerMode === "PREPARE" ? "I'm ready to answer" : $_("START")}</button
                  >
                </div>
              {:else if ttsButtonState === "wait"}
                <div style="display:flex;flex-direction:column;align-items:center;">
                  {#if showEarlyStopWarning}
                    <p style="color:#c0392b;font-size:0.85rem;max-width:280px;margin:0 0 0.5rem;text-align:center;">
                      {$_("EARLY_STOP_WARNING")}<br>{$_("EARLY_STOP_WARNING_2")}
                    </p>
                  {/if}
                  <button
                    class="btn btn-danger btn-lg btn-cta"
                    on:click={handleStopClick}><MicSvg />{$_("STOP")}</button
                  >
                </div>
              {:else if ttsButtonState === "recording"}
                <div style="display:flex;flex-direction:column;align-items:center;">
                  {#if showEarlyStopWarning}
                    <p style="color:#c0392b;font-size:0.85rem;max-width:280px;margin:0 0 0.5rem;text-align:center;">
                      {$_("EARLY_STOP_WARNING")}<br>{$_("EARLY_STOP_WARNING_2")}
                    </p>
                  {/if}
                  <button
                    class="btn btn-danger btn-lg btn-cta"
                    on:click={handleStopClick}><MicSvg />{$_("STOP")}</button
                  >
                </div>
              {/if}
              <!-- {#if ttsButtonState === "stop" || ttsButtonState === "done"}
            <button
              class="btn btn-primary btn-lg"
              style="font-size: 32px;"
              on:click={submitResult}
              ><i
                class="fas fa-check pr-3"
                style="font-size: 32px;"
              />{$_("SUBMIT")}</button
            >
          {/if} -->
            </div>
          </div>
          <div class="row justify-content-center">
            <div class="col col-auto">
              <SpeechToTextOpus
                bind:this={speechToTextOpus}
                holdPrepare={holdPrepareTimer}
                on:audioControlDuration={(event) => {
                  $audioControlDuration = event.detail.audioControlDuration;
                  formatResult($recognitionResultsFinal);
                }}
                on:prepareTimerExpired={startRecognition}
                on:recordingStop={() => {
                  console.log("SpeechToTextOpus -> on:recordingStop");
                  stopRecognitionTimeout();
                }}
                on:recordingError={(event) => {
                  const reason = event?.detail?.reason || "MIC_DENIED";
                  console.warn("[STT-DIAG] SpeechToTextOpus -> recordingError", event?.detail);
                  ttsButtonState = "error";
                  isProcessing = false;
                  isSubmitting = false;
                  canRecordNoAudio = true;
                  noAudioReason = reason;
                  submitErrorMessage = $_("MICROPHONE_ERROR");
                  trackError("speech capture failed", new Error(reason), {
                    step: "capture",
                    noAudioReason: reason,
                  });
                }}
              />
            </div>
          </div>
        {/if}
      </div>

      {#if ttsButtonState === "done"}
        <div class="row" style="display: none;">
          <div class="col">
            <div class="card">
              <div class="card-header">
                DEBUG Result: <div style="display:none;">
                  {#key aggregateScore}{JSON.stringify(aggregateScore)}{/key}
                </div>
              </div>
              <div class="card-body">
                <div id="resultDivContent">
                  <div class="score">
                    Confidence:
                    <span>{aggregateScore?.confidence}</span>
                  </div>
                  <div class="score">
                    Accuracy:
                    <span>{aggregateScore?.accuracyScore}</span>
                  </div>
                  <div class="score">
                    Fluency:
                    <span>{aggregateScore?.fluencyScore}</span>
                  </div>
                  <div class="score">
                    Completeness:
                    <span>{aggregateScore?.completenessScore}</span>
                  </div>

                  <div class="score">
                    Pronunciation:
                    <span>{aggregateScore?.pronScore}</span>
                  </div>

                  <div class="score">
                    Audio Duration:
                    <span>{aggregateScore?.audioDuration}</span>
                  </div>

                  <div class="score">
                    STT Duration:
                    <span>{aggregateScore?.duration}</span>
                  </div>

                  <div class="score">
                    Word Duration Sum:
                    <span>{aggregateScore?.wordDurationSum}</span>
                  </div>

                  <div class="score">
                    Word Count:
                    <span>{aggregateScore?.wordCount}</span>
                  </div>

                  <div class="score">
                    Word Per Minute (text only):
                    <span>{aggregateScore?.wordPerMinute}</span>
                  </div>

                  <div class="score">
                    Word Per Minute (words only):
                    <span>{aggregateScore?.wordPerMinuteWordsOnly}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3" style="display: none;">
          <div class="col">
            <div class="card">
              <div class="card-header">DEBUG Parts:</div>
              <div class="card-body">
                {#each parts as p, i}
                  <div>
                    {i + 1} - {#if p.confidence}
                      {p?.confidence.toFixed(2)}
                    {/if} - {p.displayText}
                    <span style="display: none;">{JSON.stringify(p)}</span>
                  </div>
                {/each}
              </div>
            </div>
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <!-- WRITING: confirm submitting under the minimum word count -->
  {#if showUnderWordCountModal}
    <div
      class="writing-modal-backdrop"
      on:click|self={() => (showUnderWordCountModal = false)}
    >
      <div class="writing-modal" role="dialog" aria-modal="true">
        <h5 class="writing-modal-title">{$_("UNDER_WORD_COUNT_TITLE")}</h5>
        <p class="writing-modal-text">
          {$_("UNDER_WORD_COUNT_MESSAGE", {
            values: { count: writingWordCount, min: writingMinWords },
          })}
        </p>
        <div class="writing-modal-actions">
          <button
            class="btn writing-modal-btn writing-modal-btn-secondary"
            on:click={() => (showUnderWordCountModal = false)}
            >{$_("KEEP_WRITING")}</button
          >
          <button class="btn writing-modal-btn writing-modal-btn-primary" on:click={confirmUnderWordCountSubmit}
            >{$_("SUBMIT_ANYWAY")}</button
          >
        </div>
      </div>
    </div>
  {/if}

  <!-- FILL_THE_GAP, FILL_THE_LETTERS and DRAG_WORDS: confirm submitting with
       blanks left. Blanks do not block the submit, so this is the student's
       decision, not a gate. The copy is deliberately generic: it does not say
       how many answers are blank, and it says "continue" rather than "submit"
       so it fits a page, section or whole test. -->
  {#if showIncompleteGapModal}
    <div
      class="writing-modal-backdrop"
      on:click|self={() => (showIncompleteGapModal = false)}
    >
      <div class="writing-modal" role="dialog" aria-modal="true">
        <h5 class="writing-modal-title">{$_("CONFIRM_CONTINUE_TITLE")}</h5>
        <p class="writing-modal-text">{$_("CONFIRM_CONTINUE_MESSAGE")}</p>
        <div class="writing-modal-actions">
          <button
            class="btn writing-modal-btn writing-modal-btn-secondary"
            on:click={() => (showIncompleteGapModal = false)}
            >{$_("KEEP_ANSWERING")}</button
          >
          <button
            class="btn writing-modal-btn writing-modal-btn-primary"
            on:click={confirmIncompleteGapSubmit}>{$_("CONTINUE")}</button
          >
        </div>
      </div>
    </div>
  {/if}

  <!-- Report Issue Button and Modal -->
  <ReportIssueModal
    instanceId={instanceId} 
    exerciseId={exerciseId} 
    exerciseType={exerciseDetail?.exerciseType || ""}
    exerciseName={exerciseDetail?.name || ""}
    currentStep={($instanceStatusData?.countUniqueDone || 0) + 1}
    totalSteps={$instanceStatusData?.countTotal || null}
    buttonState={ttsButtonState || ""}
    invitationNumber={$invitationNumber || ""}
  />
</div>

<style>
  :global(.jumbotron) {
    padding-top: 0rem !important;
    padding-bottom: 0rem !important;
    margin-bottom: 1rem !important;
  }

  /* ── INTRO / Directions page ── */
  .intro-directions {
    text-align: center;
    padding: 2rem 1rem 1rem;
  }

  .intro-title {
    font-size: 2.4rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  .intro-desc {
    font-size: 1rem;
    color: #475569;
    max-width: 600px;
    margin: 0 auto 2rem;
    line-height: 1.7;
  }

  .intro-stats {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }

  .stat-card {
    background: #ffffff;
    border: 1.5px solid #e2eaef;
    border-radius: 14px;
    padding: 1.4rem 2.5rem;
    min-width: 180px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .stat-label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    color: #94a3b8;
    text-transform: uppercase;
    margin: 0 0 0.5rem;
  }

  .stat-value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #3b82f6;
    margin: 0;
    line-height: 1;
  }

  .btn-intro-start {
    background-color: #00a094;
    color: #ffffff;
    border: none;
    padding: 0.85rem 3.5rem;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    border-radius: 12px;
    cursor: pointer;
    transition: filter 0.2s ease, transform 0.2s ease;
    box-shadow: 0 6px 16px rgba(0, 160, 148, 0.3);
  }

  .btn-intro-start:hover {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }

  .btn-intro-start:disabled {
    opacity: 1;
    filter: none;
    transform: none;
    cursor: not-allowed;
  }

  .state-blink {
    animation: state-blink 1s step-start infinite;
  }

  @keyframes state-blink {
    50% {
      opacity: 0.45;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .state-blink {
      animation: none;
    }
  }

  .exercise-loading-shell {
    max-width: 760px;
    margin: 1.25rem auto 2rem;
    padding: 0.75rem 1rem 1.5rem;
  }

  .exercise-loading-pass-through {
    min-height: 1px;
  }

  .exercise-loading-columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .loading-column-pane {
    height: 318px;
    border-radius: 12px;
  }

  .loading-column-pane-editor {
    height: 360px;
  }

  .loading-shimmer {
    position: relative;
    border-radius: 14px;
    background: #d9e2ea;
    overflow: hidden;
  }

  .loading-shimmer::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    animation: assessment-loading-shimmer 1.2s infinite;
  }

  .loading-submit {
    width: 288px;
    max-width: 90%;
    height: 58px;
    margin: 1rem auto 0;
    border-radius: 12px;
  }

  .overview-status-card {
    width: 100%;
    max-width: 560px;
    margin: 1rem auto 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1.25rem 1rem;
    background: #f8fbff;
    border: 1px solid #dde9f2;
    border-radius: 14px;
  }

  @media (max-width: 768px) {
    .exercise-loading-columns {
      grid-template-columns: 1fr;
    }

    .loading-column-pane,
    .loading-column-pane-editor {
      height: 220px;
    }

    .intro-stats {
      flex-wrap: nowrap;
      gap: 0.5rem;
    }

    .intro-stats .stat-card {
      flex: 1 1 0;
      min-width: 0;
      padding: 1rem 0.5rem;
    }

    .intro-stats .stat-label {
      font-size: 0.58rem;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }
  }

  @keyframes assessment-loading-shimmer {
    100% {
      transform: translateX(100%);
    }
  }

  .btn-cta {
    font-size: 1rem !important;
    font-weight: 700;
    padding: 0.75rem 2.5rem;
    border-radius: 12px;
    letter-spacing: 0.05em;
  }

  .listen-media-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    overflow-x: auto;
  }

  .listen-media-center.mt-2.mb-2 {
    margin-bottom: 16px !important;
  }

  .speaking-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x pan-y;
  }

  .speaking-card :global(table) {
    display: block;
    width: max-content !important;
    min-width: max-content !important;
    max-width: none !important;
    table-layout: auto;
  }

  .prompt-audio-first {
    color: #64748b;
    font-size: 0.85rem;
    margin: 0 0 0.5rem;
    text-align: center;
  }

  /* Two classes so it outranks the later `.textToRead { text-align: left }`. */
  .speaking-card .listen-repeat-prompt {
    text-align: center;
  }

  .listen-repeat-progress {
    text-align: center;
    font-weight: 600;
    margin: 0.5rem 0 0;
  }

  .speaking-card :global(img),
  .speaking-card :global(video),
  .speaking-card :global(iframe),
  .speaking-card :global(svg),
  .speaking-card :global(canvas) {
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }

  @media (min-width: 992px) {
    .speaking-card {
      max-width: 55%;
    }
  }

  .question-image-layout {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
  }

  .question-image-col {
    flex: 0 0 auto;
    max-width: 50%;
  }

  .question-text-col {
    flex: 1;
  }

  .lwi-layout {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .lwi-media-col {
    flex: 1 1 auto;
    max-width: 100%;
    min-width: 0;
  }

  .lwi-options-col {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    align-self: stretch;
  }

  .lwi-media-col :global(.customImage) {
    display: block;
    width: min(100%, 720px);
    max-width: 100%;
    max-height: 450px;
    height: auto;
    object-fit: contain;
    margin: 0 auto;
  }

  @media (max-width: 992px) {
    .lwi-media-col,
    .lwi-options-col {
      flex: 1 1 100%;
      min-width: 0;
      max-width: 100%;
    }

    .lwi-media-col :global(.customImage) {
      width: min(100%, 640px);
      margin: 0 auto;
    }
  }

  @media (min-width: 768px) {
    .customImage {
      max-width: 600px;
      max-height: 450px;
    }
  }

  @media (max-width: 768px) {
    .customImage {
      max-width: 100%;
    }
  }

  .answer-warning {
    color: #c0392b;
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0;
    text-align: center;
  }

  .submit-error {
    color: #c0392b;
    font-size: 0.9rem;
    font-weight: 500;
    margin: 0;
    max-width: 320px;
    text-align: center;
  }
  /* ── FILL_THE_GAP ── */
  .gap-exercise {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.1rem;
    height: 100%;
  }

  .gap-row {
    margin-top: 0.5rem;
    align-items: flex-start;
  }

  @media (min-width: 992px) {
    .gap-prompt-col {
      order: 1;
    }

    .gap-answer-col {
      order: 2;
    }
  }

  .gap-prompt-col,
  .gap-answer-col {
    display: flex;
    flex-direction: column;
  }

  .gap-answer-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.1rem;
    height: 100%;
  }

  .gap-question-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.1rem;
    margin-bottom: 0.75rem;
  }

  .gap-question-card,
  .question-item {
    overflow: hidden;
  }

  .gap-question-card,
  .question-media-card {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .gap-question-card .textToRead,
  .question-text .textToRead,
  .question-media-text,
  .gap-question-card :global(p),
  .gap-question-card :global(div),
  .gap-question-card :global(span),
  .gap-question-card :global(td),
  .gap-question-card :global(th),
  .question-text :global(p),
  .question-text :global(div),
  .question-text :global(span),
  .question-text :global(td),
  .question-text :global(th),
  .question-media-card :global(p),
  .question-media-card :global(div),
  .question-media-card :global(span),
  .question-media-card :global(td),
  .question-media-card :global(th) {
    white-space: normal !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    text-transform: none !important;
  }

  .gap-question {
    margin-bottom: 1rem;
  }

  .gap-passage {
    line-height: 2.4;
  }

  /* Inline fields are injected via @html, so they need :global selectors. The
     fallback fields in the answer card are real elements but must look the
     same, since a single exercise can show both at once. The explicit
     user-select keeps typed text selectable inside .no-select parents. */
  .gap-content :global(.gap-field),
  .gap-answer-card .gap-field {
    display: inline-block;
    min-width: 140px;
    max-width: 260px;
    margin: 0 0.25rem;
    padding: 0.25rem 0.6rem;
    font-size: 1rem;
    color: #1e293b;
    background: #ffffff;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    vertical-align: baseline;
    -webkit-user-select: text;
    user-select: text;
  }

  .gap-content :global(.gap-field:focus),
  .gap-answer-card .gap-field:focus {
    outline: none;
    border-color: #00a094;
    box-shadow: 0 0 0 2px rgba(0, 160, 148, 0.15);
  }

  /* Fields dropped in mid-sentence by a __N__ placeholder. They sit on the
     text baseline, shrink to the line height, and never stretch the passage
     wider than its column. */
  .gap-content :global(.gap-inline-field) {
    min-width: 7.5rem;
    max-width: 100%;
    padding: 0.15rem 0.5rem;
    line-height: 1.5;
    vertical-align: middle;
  }

  .gap-content :global(select.gap-inline-field) {
    padding-right: 0.35rem;
  }

  /* .no-select kills pointer events on the whole question card (anti-copy);
     the placeholder fields inside it still have to be typeable. */
  .no-select :global(.gap-field) {
    pointer-events: auto;
  }

  .gap-fallback-list {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
  }

  .gap-fallback-item {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  .gap-fallback-label {
    display: block;
    margin: 0;
    font-weight: 600;
    color: #475569;
    text-transform: none;
    letter-spacing: normal;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
    width: 100%;
  }

  .gap-answer-card .gap-field {
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  /* ── WRITING ── */
  .writing-row {
    margin-top: 0.5rem;
    align-items: flex-start;
  }

  .writing-prompt-col,
  .writing-editor-col {
    display: flex;
    flex-direction: column;
  }

  .writing-prompt {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    height: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .writing-prompt :global(img) {
    display: block;
    max-width: 100%;
    height: auto;
    object-fit: contain;
    margin: 1rem auto 0;
  }

  .writing-prompt :global(figure),
  .writing-prompt :global(picture) {
    max-width: 100%;
  }

  .writing-prompt :global(svg),
  .writing-prompt :global(canvas),
  .writing-prompt :global(video),
  .writing-prompt :global(iframe) {
    max-width: 100%;
    height: auto;
  }

  .writing-editor-card {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    overflow: hidden;
    height: auto;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .writing-editor-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0.5rem 1rem;
    border-top: 1px solid #e2eaef;
    background: #f8fafc;
  }

  .writing-word-count {
    font-size: 0.85rem;
    font-weight: 700;
    color: #16a34a;
  }

  .writing-word-count.under-minimum {
    color: #d97706;
  }

  .writing-warning {
    margin: 0;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #ffffff;
    background: #dc2626;
    text-align: center;
  }

  .writing-warning-below {
    border-top: 1px solid rgba(255, 255, 255, 0.16);
  }

  .writing-textarea {
    flex: 1;
    width: calc(100% - 2rem);
    height: 320px;
    min-height: 320px;
    min-width: 0;
    margin: 1rem;
    padding: 1rem;
    border: 1px solid #d7e3eb;
    border-radius: 8px;
    resize: vertical !important;
    overflow: auto !important;
    font-size: 1rem;
    line-height: 1.7;
    color: #1e293b;
  }

  .writing-textarea:focus {
    outline: none;
  }

  @media (min-width: 992px) {
    .writing-row .writing-prompt-col {
      flex: 0 0 58%;
      max-width: 58%;
    }

    .writing-row .writing-editor-col {
      flex: 0 0 42%;
      max-width: 42%;
    }
  }

  .writing-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1080;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.5);
  }

  .writing-modal {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    max-width: 420px;
    width: calc(100% - 2rem);
    padding: 1.5rem;
  }

  .writing-modal-title {
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 0.75rem;
  }

  .writing-modal-text {
    color: #475569;
    margin-bottom: 1.25rem;
  }

  .writing-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .writing-modal-btn {
    min-height: 52px;
    min-width: 0;
    padding: 0.74rem 1.6rem;
    font-size: 1.05rem;
    font-weight: 700;
    border-radius: 8px;
    line-height: 1.2;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease;
  }

  .writing-modal-btn-secondary {
    background-color: #dadada !important;
    border: 2px solid #dadada !important;
    color: #484649 !important;
    box-shadow: none;
  }

  .writing-modal-btn-secondary:hover {
    background-color: #dadada !important;
    border-color: #dadada !important;
    color: #484649 !important;
    transform: none;
  }

  .writing-modal-btn-primary {
    background-color: #00a094;
    color: #ffffff;
    border: 0;
    box-shadow: 0 6px 16px rgba(0, 160, 148, 0.3);
  }

  .writing-modal-btn-primary:hover {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }

  .btn-disabled {
    background-color: #dadada !important;
    border-color: #dadada !important;
    color: #484649 !important;
  }

  .textToRead {
    text-align: left;
  }

  .mc-select-question {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 1rem;
  }

  .empty-question-spacer {
    margin-bottom: 16px;
  }

  .no-select {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    pointer-events: none;
  }

  /* .no-select disables pointer events (anti-copy), which also blocked the
     audio controls / "Listen" button rendered inside the split-view media
     cards (MULTICHOICE_COMBINED, FILL_THE_GAP). Re-enable them there. */
  .no-select .listen-media-center {
    pointer-events: auto;
  }

  #pageHeader {
    position: sticky;
    top: 0;
    margin-top: 16px;
    background-color: #f1f7fc;
    transition: all 0.3s ease;
    z-index: 999;
    --header-height: 160px;
  }

  .question-progress-text {
    text-align: right;
    font-size: 0.84rem;
    font-weight: 700;
    color: rgba(51, 65, 85, 0.7);
    text-transform: uppercase;
    line-height: 1.2;
    padding: 0.25rem 0;
  }

  .top-status-bar {
    padding-bottom: 0;
  }

  .progress-details-center {
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }

  .top-status-timer {
    min-height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 24px;
  }

  @media (max-width: 767px) {
    .top-status-bar {
      row-gap: 0.3rem;
    }

    .top-status-timer,
    .question-progress-text {
      text-align: center;
    }

    .top-status-timer {
      justify-content: center;
    }
  }

  /* Show the "full" header by default, hide the collapsed version */
  .header-full {
    display: block;
    padding: 1rem;
  }
  .header-collapsed {
    display: none;
    padding: 0.5rem;
    background-color: #ddd;
  }

  .hidden {
    display: none;
  }

  .bigger-bottom {
    min-height: 12em;
  }

  .list-group.list-group-numbered {
    counter-reset: option-counter;
    padding-left: 0;
    margin-bottom: 0;
    margin-left: auto;
    margin-right: auto;
    background: transparent;
    border: none;
  }

  @media (min-width: 768px) {
    .mc-select-question {
      width: min(50vw, 760px);
    }

    .list-group.list-group-numbered {
      width: min(50vw, 760px);
    }
    .questions-section .list-group.list-group-numbered {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    .mc-select-question {
      width: 100%;
    }

    .list-group.list-group-numbered {
      width: 100%;
    }
  }

  .list-group.list-group-numbered > .list-group-item.score {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 12px;
    margin-bottom: 0.5rem;
    padding: 0.75rem 1.1rem;
    min-height: 50px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    color: #1f3d56;
    transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.15s ease, border-color 0.15s ease;
  }

  .list-group.list-group-numbered > .list-group-item.score:hover {
    background: #f9fdff;
    border-color: #00a098;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.15s ease, border-color 0.15s ease;
  }

  .list-group.list-group-numbered > .list-group-item.score.active {
    background: #e3fbff;
    border-color: #00a098;
  }

  .list-group.list-group-numbered > .list-group-item.score::before {
    content: "";
    width: 17px;
    height: 17px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    flex-shrink: 0;
    display: inline-block;
    background-color: transparent;
    transition: background-color 0.15s, transform 0.15s;
  }

  .list-group.list-group-numbered > .list-group-item.score.active::before {
    border-color: #00a098;
    background-color: #00a098;
    transform: scale(0.9);
  }

  .list-group.list-group-numbered > .list-group-item.score:hover::before {
    border-color: #00a098;
  }

  /* Sticky sidebar layout for MULTICHOICE_COMBINED */
  .mc-combined-outer {
    /* intentionally no height/overflow — page scrolls naturally */
  }

  .exercise-container {
    max-width: 85vw !important;
    width: 85vw !important;
  }

  .exercise-container.writing-container {
    max-width: 90vw !important;
    width: 90vw !important;
  }

  .mc-combined-container {
    /* intentionally no height/overflow */
  }

  .mc-combined-jumbotron-row {
    /* intentionally no height/overflow */
  }

  .mc-combined-col {
    /* intentionally no height/overflow */
  }

  .mc-combined-jumbotron {
    /* intentionally no height/overflow */
  }

  .mc-combined-footer {
    background: #f1f7fc;
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }

  /* Two-column layout for MULTICHOICE_COMBINED */
  .mc-combined-row {
    align-items: flex-start;
  }

  .passage-section {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 992px) {
    .mc-combined-row {
      align-items: flex-start;
    }

    .mc-combined-row .passage-section {
      flex: 0 0 55%;
      max-width: 55%;
    }

    .mc-combined-row .questions-section {
      flex: 0 0 45%;
      max-width: 45%;
    }

    .passage-section {
      position: sticky;
      top: var(--header-height, 160px);
      max-height: calc(100vh - var(--header-height, 160px) - 20px);
      overflow-y: auto;
    }

    .questions-section {
      overflow: visible;
      padding-bottom: 2rem;
    }
  }

  .passage-content {
    background: #ffffff;
    padding: 1.5rem;
    border-radius: 8px;
    border-left: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x pan-y;
    overscroll-behavior-x: contain;
  }

  .passage-image-wrap {
    width: 100%;
    overflow: hidden;
  }

  .passage-content :global(img) {
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }

  .passage-content.audio-only {
    background: transparent;
    border-left: none;
    box-shadow: none;
    padding: 0;
  }

  .passage-content :global(p),
  .passage-content :global(div),
  .passage-content :global(span) {
    text-align: left !important;
  }

  .passage-content :global(table) {
    display: block;
    width: max-content !important;
    min-width: max-content !important;
    max-width: none !important;
    table-layout: auto;
  }

  .questions-section {
    display: flex;
    flex-direction: column;
    overflow: visible;
    min-width: 0;
  }

  .question-media-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .question-media-text {
    margin-bottom: 1rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .question-media-card :global(*) {
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  /* Some authored HTML includes uppercase/nowrap utility classes or inline
     styles. Keep question cards readable by forcing normal wrapping/case. */
  .question-media-card :global(li),
  .question-media-card :global(h1),
  .question-media-card :global(h2),
  .question-media-card :global(h3),
  .question-media-card :global(h4),
  .question-media-card :global(h5),
  .question-media-card :global(h6),
  .question-item :global(p),
  .question-item :global(div),
  .question-item :global(span),
  .question-item :global(li),
  .question-item :global(td),
  .question-item :global(th),
  .question-item .textToRead {
    white-space: normal !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    text-transform: none !important;
  }

  .question-media-card :global(.text-uppercase),
  .question-item :global(.text-uppercase) {
    text-transform: none !important;
  }

  .gap-question-card :global(table),
  .question-text :global(table),
  .question-item :global(table),
  .question-media-card :global(table) {
    display: block;
    width: max-content;
    min-width: 100%;
    max-width: none;
  }

  .question-text {
    min-width: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .question-text :global(table) {
    width: max-content;
    min-width: 100%;
    max-width: none;
  }

  .question-media-card :global(img),
  .question-media-card :global(video),
  .question-media-card :global(iframe),
  .question-media-card :global(svg),
  .question-media-card :global(canvas) {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .question-item {
    margin-bottom: 0;
    padding: 1rem;
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .question-text {
    margin-bottom: 1rem;
    min-width: 0;
    width: 100%;
  }

  .question-options {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }

  .question-options .list-group-item {
    min-height: 50px;
  }

  @media (max-width: 992px) {
    .exercise-container {
      width: 95vw !important;
      max-width: 95vw !important;
    }

    .question-media-card {
      padding: 0.5rem;
    }

    .question-media-card :global(img) {
      width: 100%;
      height: auto;
    }

    .passage-content {
      margin-bottom: 2rem;
    }

    .passage-section {
      position: static;
      order: -1; /* Put passage first on mobile */
    }
  }

  .passage-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    background: transparent;
  }

  .passage-tabs-nav {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .passage-arrow {
    background: none;
    border: 1px solid #d3dfe6;
    border-radius: 6px;
    color: #00a098;
    font-size: 1.3rem;
    line-height: 1;
    padding: 0.25rem 0.55rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.15s, border-color 0.15s;
  }

  .passage-arrow:hover:not(:disabled) {
    background-color: #e2fbff;
    border-color: #00a098;
  }

  .passage-arrow:disabled {
    opacity: 0.3;
    cursor: default;
  }

  .passage-tab {
    border: 1px solid #d3dfe6;
    background-color: #ffffff;
    padding: 0.4rem 0.75rem;
    border-radius: 6px;
    color: #00a098;
    font-weight: 600;
    cursor: pointer;
    min-width: 90px;
    text-transform: uppercase;
    font-size: 0.765em;
  }

  .passage-content.swipeable {
    cursor: default;
    user-select: none;
    position: relative;
  }

  .passage-float-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #d3dfe6;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    color: #00a098;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: background-color 0.15s, border-color 0.15s, opacity 0.15s;
    padding: 0;
    line-height: 1;
  }

  .passage-float-arrow.left  { left: -14px; }
  .passage-float-arrow.right { right: -14px; }

  .passage-float-arrow:hover:not(:disabled) {
    background-color: #e2fbff;
    border-color: #00a098;
  }

  .passage-float-arrow:disabled {
    opacity: 0;
    pointer-events: none;
  }

  .passage-tab.active {
    border-color: #00a098;
    background-color: #e2fbff;
    color: #007f88;
  }

  .passage-tabs-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  /* Dialogue / chat passage styling */
  .dialogue-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.25rem 0;
  }

  .dialogue-row {
    display: flex;
    flex-direction: column;
    max-width: 100%;
  }

  .dialogue-row.left {
    align-items: flex-start;
  }

  .dialogue-row.right {
    align-items: flex-end;
  }

  .dialogue-meta {
    font-size: 0.78rem;
    color: #64748b;
    margin-bottom: 0.2rem;
  }

  .dialogue-bubble {
    max-width: 75%;
    padding: 0.6rem 0.9rem;
    border-radius: 12px;
    font-size: 0.92rem;
    line-height: 1.55;
    color: #1e293b;
  }

  .bubble-left {
    background: #f1f5f9;
    border-top-left-radius: 2px;
    text-align: left;
  }

  .bubble-right {
    background: #e8f5f5;
    border-top-right-radius: 2px;
    text-align: right;
  }

  /* Email passage styling */
  .email-wrapper {
    display: flex;
    flex-direction: column;
  }

  .email-header-block {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding-bottom: 0.85rem;
    margin-bottom: 0.85rem;
    border-bottom: 1px solid #d1d5db;
  }

  .email-header-row {
    display: flex;
    gap: 0.5rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .email-label {
    font-weight: 700;
    color: #1e293b;
    min-width: 4.5rem;
    flex-shrink: 0;
  }

  .email-value {
    color: #374151;
  }

  .email-body {
    font-size: 0.95rem;
    line-height: 1.7;
  }
</style>
